import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { 
  Lock, Mail, FileText, Users, Calendar, Eye, EyeOff, Loader2, RefreshCw,
  ChevronRight, CheckCircle, Clock, Banknote, Film, Play, Link as LinkIcon,
  Copy, ExternalLink, FolderOpen, Upload, FileCheck, ChevronDown, Download, CreditCard, UserPlus, Trash2,
  Star, MessageSquare, X, User, Phone, Building, MapPin, Euro, Search, BarChart3, TrendingUp, 
  Edit2, Save, PlusCircle, MapPinned, CalendarDays, AlertCircle, Heart, XCircle
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const ADMIN_EMAIL = process.env.REACT_APP_ADMIN_EMAIL;
const ADMIN_PASSWORD = process.env.REACT_APP_ADMIN_PASSWORD;

const statusSteps = [
  { id: 'soumission', label: 'Soumission', icon: FileText, color: 'bg-gray-500' },
  { id: 'evaluation', label: 'Évaluation', icon: Clock, color: 'bg-amber-500' },
  { id: 'levee', label: 'En cours de levée', icon: Banknote, color: 'bg-blue-500' },
  { id: 'virement', label: 'Virement émis', icon: CreditCard, color: 'bg-emerald-500' },
  { id: 'production', label: 'Production', icon: Film, color: 'bg-purple-500' },
  { id: 'diffusion', label: 'Diffusion', icon: Play, color: 'bg-green-500' },
];

const AdminPage = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  
  const [activeTab, setActiveTab] = useState('contacts');
  const [contacts, setContacts] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [updatingProject, setUpdatingProject] = useState(null);
  const [generatingToken, setGeneratingToken] = useState(null);
  const [copiedToken, setCopiedToken] = useState(null);
  const [expandedProject, setExpandedProject] = useState(null);
  const [expandedDocProject, setExpandedDocProject] = useState(null);
  const [uploadingContract, setUploadingContract] = useState(null);
  const fileInputRef = useRef(null);
  const [producers, setProducers] = useState([]);
  const [newProducerEmail, setNewProducerEmail] = useState('');
  const [newProducerName, setNewProducerName] = useState('');
  const [creatingProducer, setCreatingProducer] = useState(false);
  const [showCreateProducer, setShowCreateProducer] = useState(false);
  const [copiedProducerToken, setCopiedProducerToken] = useState(null);
  const [deletingProject, setDeletingProject] = useState(null);
  const [deletingProducer, setDeletingProducer] = useState(null);
  const [showAddProject, setShowAddProject] = useState(null);
  const [newProjectTitle, setNewProjectTitle] = useState('');
  const [newProjectCategory, setNewProjectCategory] = useState('');
  const [addingProject, setAddingProject] = useState(false);
  const [uploadingTransferProof, setUploadingTransferProof] = useState(null);
  const [allocatedBudgets, setAllocatedBudgets] = useState({});
  const [editingBudget, setEditingBudget] = useState(null);
  const [budgetValue, setBudgetValue] = useState('');
  const [selectedProducer, setSelectedProducer] = useState(null);
  const [producerComment, setProducerComment] = useState('');
  const [savingComment, setSavingComment] = useState(false);
  const [projectRatings, setProjectRatings] = useState({});
  const [projectNotes, setProjectNotes] = useState({});
  const [editingNote, setEditingNote] = useState(null);
  const [noteText, setNoteText] = useState('');
  
  // Search states
  const [projectSearch, setProjectSearch] = useState('');
  const [producerSearch, setProducerSearch] = useState('');
  
  // Editable producer profile
  const [editingProducerProfile, setEditingProducerProfile] = useState(false);
  const [editedProducer, setEditedProducer] = useState({});
  const [savingProducer, setSavingProducer] = useState(false);
  
  // Donations
  const [donations, setDonations] = useState([]);
  const [donationStats, setDonationStats] = useState({ totalAmount: 0, totalCount: 0, pendingCount: 0 });
  const [selectedDonation, setSelectedDonation] = useState(null);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [editingDonation, setEditingDonation] = useState(false);
  const [editedDonation, setEditedDonation] = useState({});
  const [savingDonation, setSavingDonation] = useState(false);
  const [donationYearFilter, setDonationYearFilter] = useState(new Date().getFullYear().toString());
  const [showAddDonation, setShowAddDonation] = useState(false);
  const [newDonation, setNewDonation] = useState({
    amount: '',
    donorType: 'individual',
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'France',
    companyName: '',
    siret: '',
    status: 'confirmed',
    createdAt: new Date().toISOString().split('T')[0]
  });
  const [addingDonation, setAddingDonation] = useState(false);
  const [importingExcel, setImportingExcel] = useState(false);
  
  // Link project to producer
  const [linkingProject, setLinkingProject] = useState(null);
  const [selectedProducerForLink, setSelectedProducerForLink] = useState('');
  
  // Multiple producers per project
  const [addingProducerToProject, setAddingProducerToProject] = useState(null);
  const [producerSearchInProject, setProducerSearchInProject] = useState('');
  const [selectedProducerToAdd, setSelectedProducerToAdd] = useState(null);
  
  // Inline budget editing
  const [inlineBudgetEdit, setInlineBudgetEdit] = useState(null);
  const [inlineBudgetValue, setInlineBudgetValue] = useState('');

  const categories = [
    'Court métrage fiction',
    'Court métrage documentaire', 
    'Long métrage fiction',
    'Long métrage documentaire',
    'Série',
    'Animation',
    'Autre'
  ];

  const handleLogin = (e) => {
    e.preventDefault();
    if (email === ADMIN_EMAIL && password === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      setError('');
      sessionStorage.setItem('adminAuth', 'true');
      fetchData();
    } else {
      setError('Email ou mot de passe incorrect');
    }
  };

  // Restore session on page load
  useEffect(() => {
    const savedAuth = sessionStorage.getItem('adminAuth');
    if (savedAuth === 'true') {
      setIsAuthenticated(true);
      fetchData();
    }
  }, []);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [contactsRes, documentsRes, producersRes, donationsRes, donationStatsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/contact`),
        axios.get(`${BACKEND_URL}/api/documents`),
        axios.get(`${BACKEND_URL}/api/producers`),
        axios.get(`${BACKEND_URL}/api/donations`),
        axios.get(`${BACKEND_URL}/api/donations/stats`)
      ]);
      setContacts(contactsRes.data || []);
      setDocuments(documentsRes.data || []);
      setProducers(producersRes.data || []);
      setDonations(donationsRes.data || []);
      setDonationStats(donationStatsRes.data || { totalAmount: 0, totalCount: 0, pendingCount: 0 });
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const deleteProject = async (projectId, projectTitle) => {
    if (!window.confirm(`Supprimer le projet "${projectTitle}" et tous ses documents ?`)) return;
    setDeletingProject(projectId);
    try {
      await axios.delete(`${BACKEND_URL}/api/contact/${projectId}`);
      await fetchData();
    } catch (err) {
      console.error('Error deleting project:', err);
    } finally {
      setDeletingProject(null);
    }
  };

  const deleteProducer = async (email, name) => {
    if (!window.confirm(`Supprimer le producteur "${name || email}" et tous ses projets/documents ?`)) return;
    setDeletingProducer(email);
    try {
      await axios.delete(`${BACKEND_URL}/api/producer/${encodeURIComponent(email)}`);
      await fetchData();
    } catch (err) {
      console.error('Error deleting producer:', err);
    } finally {
      setDeletingProducer(null);
    }
  };

  const addProjectToProducer = async (producerEmail) => {
    if (!newProjectTitle) return;
    setAddingProject(true);
    try {
      await axios.post(`${BACKEND_URL}/api/producer/create-project`, {
        email: producerEmail,
        projectTitle: newProjectTitle,
        category: newProjectCategory || 'Non défini'
      });
      await fetchData();
      setNewProjectTitle('');
      setNewProjectCategory('');
      setShowAddProject(null);
    } catch (err) {
      console.error('Error adding project:', err);
    } finally {
      setAddingProject(false);
    }
  };

  const uploadTransferProof = async (projectId, file) => {
    if (!file) return;
    setUploadingTransferProof(projectId);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('projectId', projectId);
      formData.append('documentType', 'Preuve de virement');
      formData.append('uploadedBy', 'admin');
      
      await axios.post(`${BACKEND_URL}/api/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      await fetchData();
    } catch (err) {
      console.error('Error uploading transfer proof:', err);
    } finally {
      setUploadingTransferProof(null);
    }
  };

  const updateBudget = async (projectId, budget) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/contact/${projectId}/budget`, { budget });
      setAllocatedBudgets(prev => ({ ...prev, [projectId]: budget }));
      setEditingBudget(null);
      setBudgetValue('');
      await fetchData();
    } catch (err) {
      console.error('Error updating budget:', err);
    }
  };

  const hasTransferProof = (projectId) => {
    return documents.some(d => d.projectId === projectId && d.documentType === 'Preuve de virement' && d.uploadedBy === 'admin');
  };

  const saveProjectRating = async (projectId, rating) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/contact/${projectId}/rating`, { rating });
      setProjectRatings(prev => ({ ...prev, [projectId]: rating }));
      // Update local state
      setContacts(prev => prev.map(c => c.id === projectId ? { ...c, rating } : c));
    } catch (err) {
      console.error('Error saving rating:', err);
    }
  };

  const saveProjectNote = async (projectId) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/contact/${projectId}/note`, { note: noteText });
      setContacts(prev => prev.map(c => c.id === projectId ? { ...c, internalNote: noteText } : c));
      setEditingNote(null);
      setNoteText('');
    } catch (err) {
      console.error('Error saving note:', err);
    }
  };

  const saveProducerComment = async () => {
    if (!selectedProducer) return;
    setSavingComment(true);
    try {
      await axios.patch(`${BACKEND_URL}/api/producer/${encodeURIComponent(selectedProducer.email)}/comment`, {
        comment: producerComment
      });
      // Update local state
      setProducers(prev => prev.map(p => 
        p.email === selectedProducer.email ? { ...p, adminComment: producerComment } : p
      ));
      setSelectedProducer(prev => ({ ...prev, adminComment: producerComment }));
    } catch (err) {
      console.error('Error saving comment:', err);
    } finally {
      setSavingComment(false);
    }
  };

  const getProducerTotalBudget = (producerEmail) => {
    const producerProjects = contacts.filter(c => 
      c.email?.toLowerCase() === producerEmail?.toLowerCase() && c.allocatedBudget
    );
    let total = 0;
    producerProjects.forEach(p => {
      const budgetStr = p.allocatedBudget?.replace(/[^\d]/g, '') || '0';
      total += parseInt(budgetStr) || 0;
    });
    return total;
  };

  const formatBudget = (value) => {
    if (!value) return '0 €';
    const num = parseInt(value.toString().replace(/[^\d]/g, '')) || 0;
    return num.toLocaleString('fr-FR') + ' €';
  };

  const openProducerProfile = (producer) => {
    setSelectedProducer(producer);
    setEditedProducer({ ...producer });
    setProducerComment(producer.adminComment || '');
    setEditingProducerProfile(false);
  };

  // Save edited producer profile
  const saveProducerProfile = async () => {
    if (!selectedProducer) return;
    setSavingProducer(true);
    try {
      await axios.put(`${BACKEND_URL}/api/producer/${encodeURIComponent(selectedProducer.email)}`, editedProducer);
      // Update local state
      setProducers(prev => prev.map(p => 
        p.email === selectedProducer.email ? { ...p, ...editedProducer } : p
      ));
      setSelectedProducer(prev => ({ ...prev, ...editedProducer }));
      setEditingProducerProfile(false);
      await fetchData();
    } catch (err) {
      console.error('Error saving producer:', err);
    } finally {
      setSavingProducer(false);
    }
  };

  // Link project to producer
  const linkProjectToProducer = async (projectId, producerEmail) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/contact/${projectId}/link-producer`, {
        producerEmail
      });
      await fetchData();
      setLinkingProject(null);
      setSelectedProducerForLink('');
    } catch (err) {
      console.error('Error linking project:', err);
    }
  };

  // Add producer to project (multiple producers)
  const addProducerToProject = async (projectId, producerEmail) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/contact/${projectId}/add-producer`, {
        producerEmail
      });
      await fetchData();
      setAddingProducerToProject(null);
      setProducerSearchInProject('');
    } catch (err) {
      console.error('Error adding producer to project:', err);
    }
  };

  // Remove producer from project
  const removeProducerFromProject = async (projectId, producerEmail) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/contact/${projectId}/remove-producer`, {
        producerEmail
      });
      await fetchData();
    } catch (err) {
      console.error('Error removing producer:', err);
    }
  };

  // Save inline budget
  const saveInlineBudget = async (projectId) => {
    try {
      await axios.patch(`${BACKEND_URL}/api/contact/${projectId}/budget`, { 
        budget: inlineBudgetValue 
      });
      await fetchData();
      setInlineBudgetEdit(null);
      setInlineBudgetValue('');
    } catch (err) {
      console.error('Error saving budget:', err);
    }
  };

  const getFilteredProducersForProject = () => {
    if (!producerSearchInProject) return producers;
    return producers.filter(p => 
      p.name?.toLowerCase().includes(producerSearchInProject.toLowerCase()) ||
      p.email?.toLowerCase().includes(producerSearchInProject.toLowerCase())
    );
  };

  // Calculate global stats
  const getGlobalBudget = () => {
    let total = 0;
    contacts.forEach(c => {
      if (c.allocatedBudget) {
        const num = parseInt(c.allocatedBudget.replace(/[^\d]/g, '')) || 0;
        total += num;
      }
    });
    return total;
  };

  // Get available years from donations
  const getDonationYears = () => {
    const years = new Set();
    donations.forEach(d => {
      if (d.createdAt) {
        years.add(new Date(d.createdAt).getFullYear().toString());
      }
    });
    return Array.from(years).sort((a, b) => b - a);
  };

  // Filter donations by year
  const getFilteredDonations = () => {
    if (!donationYearFilter || donationYearFilter === 'all') return donations;
    return donations.filter(d => {
      if (!d.createdAt) return false;
      return new Date(d.createdAt).getFullYear().toString() === donationYearFilter;
    });
  };

  // Get donation stats for selected year
  const getYearDonationStats = () => {
    const filtered = getFilteredDonations();
    const totalAmount = filtered.reduce((sum, d) => sum + (d.amount || 0), 0);
    const confirmedAmount = filtered.filter(d => d.status === 'confirmed').reduce((sum, d) => sum + (d.amount || 0), 0);
    const pendingAmount = filtered.filter(d => d.status === 'pending').reduce((sum, d) => sum + (d.amount || 0), 0);
    return { totalAmount, confirmedAmount, pendingAmount, count: filtered.length };
  };

  // Export donations to Excel
  const exportDonationsToExcel = () => {
    const filtered = getFilteredDonations();
    
    // Create CSV content
    const headers = ['Date', 'Référence', 'Nom', 'Prénom', 'Email', 'Téléphone', 'Type', 'Société', 'SIRET', 'Adresse', 'Code Postal', 'Ville', 'Montant (€)', 'Statut', 'Message'];
    const rows = filtered.map(d => [
      d.createdAt ? new Date(d.createdAt).toLocaleDateString('fr-FR') : '',
      d.refCode || '',
      d.lastName || '',
      d.firstName || '',
      d.email || '',
      d.phone || '',
      d.donorType === 'company' ? 'Entreprise' : 'Particulier',
      d.companyName || '',
      d.siret || '',
      d.address || '',
      d.postalCode || '',
      d.city || '',
      d.amount || 0,
      d.status === 'confirmed' ? 'Confirmé' : d.status === 'cancelled' ? 'Annulé' : 'En attente',
      d.message || ''
    ]);
    
    // BOM for Excel to recognize UTF-8
    const BOM = '\uFEFF';
    const csvContent = BOM + [headers, ...rows].map(row => 
      row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(';')
    ).join('\n');
    
    // Download file
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `dons_${donationYearFilter || 'tous'}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const getProjectsByStatus = () => {
    const stats = {};
    statusSteps.forEach(s => {
      stats[s.id] = contacts.filter(c => c.status === s.id).length;
    });
    return stats;
  };

  // Filter functions
  const filteredContacts = contacts.filter(c => {
    const search = projectSearch.toLowerCase();
    return (
      c.projectTitle?.toLowerCase().includes(search) ||
      c.name?.toLowerCase().includes(search) ||
      c.email?.toLowerCase().includes(search) ||
      c.category?.toLowerCase().includes(search)
    );
  });

  const filteredProducers = producers.filter(p => {
    const search = producerSearch.toLowerCase();
    return (
      p.name?.toLowerCase().includes(search) ||
      p.email?.toLowerCase().includes(search) ||
      p.company?.toLowerCase().includes(search)
    );
  });

  const createProducer = async () => {
    if (!newProducerEmail) return;
    setCreatingProducer(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/producer/create`, {
        email: newProducerEmail,
        name: newProducerName || null
      });
      if (response.data.token) {
        await fetchData();
        setNewProducerEmail('');
        setNewProducerName('');
        setShowCreateProducer(false);
        // Copy link to clipboard
        const link = `${window.location.origin}/producteur/${response.data.token}`;
        try {
          await navigator.clipboard.writeText(link);
          setCopiedProducerToken(response.data.token);
          setTimeout(() => setCopiedProducerToken(null), 3000);
        } catch (clipboardErr) {
          console.log('Clipboard not available, link:', link);
          // Show alert as fallback
          alert(`Producteur créé ! Lien:\n${link}`);
        }
      }
    } catch (err) {
      console.error('Error creating producer:', err);
    } finally {
      setCreatingProducer(false);
    }
  };

  const copyProducerLink = async (token) => {
    const link = `${window.location.origin}/producteur/${token}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopiedProducerToken(token);
      setTimeout(() => setCopiedProducerToken(null), 2000);
    } catch (err) {
      alert(`Lien:\n${link}`);
    }
  };

  const updateProjectStatus = async (projectId, newStatus) => {
    setUpdatingProject(projectId);
    try {
      await axios.patch(`${BACKEND_URL}/api/contact/${projectId}/status`, {
        status: newStatus
      });
      await fetchData();
    } catch (err) {
      console.error('Error updating status:', err);
    } finally {
      setUpdatingProject(null);
    }
  };

  const generateToken = async (projectId) => {
    setGeneratingToken(projectId);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/contact/${projectId}/generate-token`);
      await fetchData();
      return response.data.token;
    } catch (err) {
      console.error('Error generating token:', err);
    } finally {
      setGeneratingToken(null);
    }
  };

  const copyLink = async (token) => {
    const link = `${window.location.origin}/producteur/${token}`;
    try {
      await navigator.clipboard.writeText(link);
      setCopiedToken(token);
      setTimeout(() => setCopiedToken(null), 2000);
    } catch (err) {
      alert(`Lien:\n${link}`);
    }
  };

  const getStatusIndex = (status) => {
    const index = statusSteps.findIndex(s => s.id === status);
    return index >= 0 ? index : 0;
  };

  const getProjectDocuments = (projectId) => {
    return documents.filter(d => d.projectId === projectId);
  };

  const getDocumentsByProject = () => {
    const grouped = {};
    contacts.forEach(contact => {
      grouped[contact.id] = {
        project: contact,
        documents: documents.filter(d => d.projectId === contact.id)
      };
    });
    return grouped;
  };

  const uploadContract = async (projectId, file) => {
    if (!file) return;
    setUploadingContract(projectId);
    try {
      // Upload actual file
      const formData = new FormData();
      formData.append('file', file);
      formData.append('projectId', projectId);
      formData.append('documentType', 'Contrat définitif');
      formData.append('uploadedBy', 'admin');
      
      await axios.post(`${BACKEND_URL}/api/documents/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      await fetchData();
    } catch (err) {
      console.error('Error uploading contract:', err);
    } finally {
      setUploadingContract(null);
    }
  };

  const handleDownload = (docId) => {
    window.open(`${BACKEND_URL}/api/documents/download/${docId}`, '_blank');
  };

  const hasContract = (projectId) => {
    return documents.some(d => d.projectId === projectId && d.documentType === 'Contrat définitif' && d.uploadedBy === 'admin');
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="glass rounded-3xl p-10 max-w-md w-full"
        >
          <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-[#D4AF37]/20 flex items-center justify-center">
            <Lock size={32} className="text-[#D4AF37]" />
          </div>
          <h1 className="text-2xl font-medium text-white text-center mb-2">
            Administration
          </h1>
          <p className="text-[#71717a] text-center mb-8">
            Second Souffle Production
          </p>

          <form onSubmit={handleLogin}>
            <div className="mb-4">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Email"
                className="w-full px-4 py-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#71717a] focus:outline-none focus:border-[#D4AF37] transition-colors"
                data-testid="admin-email"
              />
            </div>
            <div className="relative mb-6">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mot de passe"
                className="w-full px-4 py-4 rounded-xl bg-white/5 border border-white/10 text-white placeholder-[#71717a] focus:outline-none focus:border-[#D4AF37] transition-colors pr-12"
                data-testid="admin-password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#71717a] hover:text-white"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>

            {error && (
              <p className="text-red-400 text-sm text-center mb-4">{error}</p>
            )}

            <button
              type="submit"
              className="btn-primary w-full"
              data-testid="admin-login-btn"
            >
              Accéder
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-page">
      <Header />
      
      <main className="pt-32 pb-20">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-10">
            <div>
              <span className="overline mb-2 block">Administration</span>
              <h1 className="text-3xl md:text-4xl font-medium text-white">
                Gestion des projets
              </h1>
            </div>
            <button
              onClick={fetchData}
              disabled={isLoading}
              className="btn-secondary mt-4 md:mt-0 flex items-center gap-2"
            >
              <RefreshCw size={18} className={isLoading ? 'animate-spin' : ''} />
              Actualiser
            </button>
          </div>

          {/* Tabs */}
          <div className="flex gap-3 mb-8 overflow-x-auto pb-2">
            <button
              onClick={() => setActiveTab('dashboard')}
              className={`px-5 py-2.5 rounded-full font-medium transition-all whitespace-nowrap text-sm ${
                activeTab === 'dashboard'
                  ? 'bg-[#D4AF37] text-[#050505]'
                  : 'bg-white/5 text-white hover:bg-white/10'
              }`}
            >
              <BarChart3 size={16} className="inline mr-2" />
              Tableau de bord
            </button>
            <button
              onClick={() => setActiveTab('contacts')}
              className={`px-5 py-2.5 rounded-full font-medium transition-all whitespace-nowrap text-sm ${
                activeTab === 'contacts'
                  ? 'bg-[#D4AF37] text-[#050505]'
                  : 'bg-white/5 text-white hover:bg-white/10'
              }`}
            >
              <Film size={16} className="inline mr-2" />
              Projets ({contacts.length})
            </button>
            <button
              onClick={() => setActiveTab('producers')}
              className={`px-5 py-2.5 rounded-full font-medium transition-all whitespace-nowrap text-sm ${
                activeTab === 'producers'
                  ? 'bg-[#D4AF37] text-[#050505]'
                  : 'bg-white/5 text-white hover:bg-white/10'
              }`}
            >
              <Users size={16} className="inline mr-2" />
              Producteurs ({producers.length})
            </button>
            <button
              onClick={() => setActiveTab('donations')}
              className={`px-5 py-3 rounded-full text-sm font-medium transition-all flex items-center ${
                activeTab === 'donations'
                  ? 'bg-[#D4AF37] text-[#050505]'
                  : 'bg-white/5 text-white hover:bg-white/10'
              }`}
            >
              <Heart size={16} className="inline mr-2" />
              Dons ({donations.length})
              {donationStats.pendingCount > 0 && (
                <span className="ml-2 px-2 py-0.5 bg-orange-500 text-white text-xs rounded-full">
                  {donationStats.pendingCount}
                </span>
              )}
            </button>
          </div>

          {/* Content */}
          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 size={40} className="text-[#D4AF37] animate-spin" />
            </div>
          ) : activeTab === 'dashboard' ? (
            /* Dashboard Tab */
            <div className="space-y-8">
              {/* Collecte de Dons Section - TOP */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="glass rounded-2xl p-6"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-white font-semibold flex items-center gap-2">
                    <Heart size={20} className="text-[#D4AF37]" />
                    Collecte de Dons
                  </h3>
                  <div className="flex items-center gap-3">
                    <select
                      value={donationYearFilter}
                      onChange={(e) => setDonationYearFilter(e.target.value)}
                      className="bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-white text-sm focus:outline-none focus:border-[#D4AF37]/50"
                    >
                      <option value="all" className="bg-[#0a0a0a]">Depuis le début</option>
                      {getDonationYears().map(year => (
                        <option key={year} value={year} className="bg-[#0a0a0a]">{year}</option>
                      ))}
                    </select>
                    <button 
                      onClick={() => setActiveTab('donations')}
                      className="text-[#D4AF37] text-sm hover:underline flex items-center gap-1"
                    >
                      Voir tout
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
                  <div className="p-4 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/20">
                    <p className="text-[#a1a1aa] text-sm mb-1">Total collecté</p>
                    <p className="text-2xl font-bold text-[#D4AF37]">{getYearDonationStats().totalAmount.toLocaleString('fr-FR')} €</p>
                  </div>
                  <div className="p-4 rounded-xl bg-green-500/10 border border-green-500/20">
                    <p className="text-[#a1a1aa] text-sm mb-1">Confirmés</p>
                    <p className="text-2xl font-bold text-green-400">{getYearDonationStats().confirmedAmount.toLocaleString('fr-FR')} €</p>
                  </div>
                  <div className="p-4 rounded-xl bg-orange-500/10 border border-orange-500/20">
                    <p className="text-[#a1a1aa] text-sm mb-1">En attente</p>
                    <p className="text-2xl font-bold text-orange-400">{getYearDonationStats().pendingAmount.toLocaleString('fr-FR')} €</p>
                  </div>
                  <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                    <p className="text-[#a1a1aa] text-sm mb-1">Nombre de dons</p>
                    <p className="text-2xl font-bold text-white">{getYearDonationStats().count}</p>
                  </div>
                </div>

                {/* Progress bar */}
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-[#a1a1aa]">Progression {donationYearFilter !== 'all' ? donationYearFilter : 'depuis le début'}</span>
                    <span className="text-white">
                      {getYearDonationStats().totalAmount > 0 
                        ? Math.round((getYearDonationStats().confirmedAmount / getYearDonationStats().totalAmount) * 100) 
                        : 0}% confirmés
                    </span>
                  </div>
                  <div className="h-3 bg-white/5 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-green-500 to-[#D4AF37] rounded-full transition-all duration-500"
                      style={{ 
                        width: `${getYearDonationStats().totalAmount > 0 
                          ? (getYearDonationStats().confirmedAmount / getYearDonationStats().totalAmount) * 100 
                          : 0}%` 
                      }}
                    />
                  </div>
                </div>
              </motion.div>

              {/* Main Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {/* Total Budget */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-[#D4AF37]/10 to-[#D4AF37]/5 border border-[#D4AF37]/20 p-6"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/20 flex items-center justify-center">
                      <Euro size={24} className="text-[#D4AF37]" />
                    </div>
                    <TrendingUp size={20} className="text-[#D4AF37]" />
                  </div>
                  <p className="text-3xl font-bold text-white mb-1">{formatBudget(getGlobalBudget())}</p>
                  <p className="text-[#a1a1aa] text-sm">Total Achats de Droits</p>
                </motion.div>

                {/* Projects Count */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 }}
                  className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-500/10 to-purple-500/5 border border-purple-500/20 p-6"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                      <Film size={24} className="text-purple-400" />
                    </div>
                    <span className="text-purple-400 text-sm font-medium">+{contacts.filter(c => {
                      const date = new Date(c.createdAt);
                      const now = new Date();
                      return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
                    }).length} ce mois</span>
                  </div>
                  <p className="text-3xl font-bold text-white mb-1">{contacts.length}</p>
                  <p className="text-[#a1a1aa] text-sm">Projets</p>
                </motion.div>

                {/* Producers Count */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-blue-500/10 to-blue-500/5 border border-blue-500/20 p-6"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                      <Users size={24} className="text-blue-400" />
                    </div>
                    <span className="text-blue-400 text-sm font-medium">{producers.filter(p => p.password).length} actifs</span>
                  </div>
                  <p className="text-3xl font-bold text-white mb-1">{producers.length}</p>
                  <p className="text-[#a1a1aa] text-sm">Producteurs</p>
                </motion.div>

                {/* Documents Count */}
                <motion.div 
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.25 }}
                  className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border border-emerald-500/20 p-6"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                      <FileText size={24} className="text-emerald-400" />
                    </div>
                    <span className="text-emerald-400 text-sm font-medium">+{documents.filter(d => {
                      const docDate = new Date(d.uploadedAt);
                      const now = new Date();
                      return docDate.getMonth() === now.getMonth() && docDate.getFullYear() === now.getFullYear();
                    }).length} ce mois</span>
                  </div>
                  <p className="text-3xl font-bold text-white mb-1">{documents.length}</p>
                  <p className="text-[#a1a1aa] text-sm">Documents</p>
                </motion.div>
              </div>

              {/* Two Column Layout */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Projects by Status - Left Column */}
                <motion.div 
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                  className="lg:col-span-2 glass rounded-2xl p-6"
                >
                  <h3 className="text-white font-semibold mb-6 flex items-center gap-2">
                    <BarChart3 size={20} className="text-[#D4AF37]" />
                    Répartition des projets
                  </h3>
                  <div className="space-y-4">
                    {statusSteps.map((step, index) => {
                      const count = contacts.filter(c => c.status === step.id).length;
                      const percentage = contacts.length > 0 ? (count / contacts.length) * 100 : 0;
                      const StepIcon = step.icon;
                      return (
                        <motion.div 
                          key={step.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: 0.35 + index * 0.05 }}
                          className="group"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-3">
                              <div className={`w-8 h-8 rounded-lg ${step.color}/20 flex items-center justify-center`}>
                                <StepIcon size={16} className={step.color.replace('bg-', 'text-').replace('-500', '-400')} />
                              </div>
                              <span className="text-white text-sm font-medium">{step.label}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-[#a1a1aa] text-sm">{Math.round(percentage)}%</span>
                              <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${step.color}/20 text-white min-w-[28px] text-center`}>
                                {count}
                              </span>
                            </div>
                          </div>
                          <div className="h-2 bg-white/5 rounded-full overflow-hidden">
                            <motion.div 
                              initial={{ width: 0 }}
                              animate={{ width: `${percentage}%` }}
                              transition={{ duration: 0.8, delay: 0.4 + index * 0.05 }}
                              className={`h-full ${step.color} rounded-full`}
                            />
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.div>

                {/* Quick Actions - Right Column */}
                <motion.div 
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 }}
                  className="space-y-4"
                >
                  {/* Quick Stats */}
                  <div className="glass rounded-2xl p-5">
                    <h4 className="text-white font-medium mb-4 flex items-center gap-2">
                      <TrendingUp size={18} className="text-emerald-400" />
                      Activité récente
                    </h4>
                    <div className="space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-[#a1a1aa] text-sm">Projets en production</span>
                        <span className="text-white font-medium">{contacts.filter(c => c.status === 'production' || c.status === 'diffusion').length}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[#a1a1aa] text-sm">Virements émis</span>
                        <span className="text-white font-medium">{contacts.filter(c => c.status === 'virement').length}</span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-[#a1a1aa] text-sm">En évaluation</span>
                        <span className="text-white font-medium">{contacts.filter(c => c.status === 'evaluation').length}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>

              {/* Recent Projects */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
                className="glass rounded-2xl p-6"
              >
                <div className="flex items-center justify-between mb-6">
                  <h3 className="text-white font-semibold flex items-center gap-2">
                    <Film size={20} className="text-[#D4AF37]" />
                    Derniers projets
                  </h3>
                  <button 
                    onClick={() => setActiveTab('contacts')}
                    className="text-[#D4AF37] text-sm hover:underline flex items-center gap-1"
                  >
                    Voir tout
                    <ChevronRight size={16} />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {contacts.slice(0, 6).map((project, index) => {
                    const status = statusSteps.find(s => s.id === project.status);
                    const StatusIcon = status?.icon || FileText;
                    return (
                      <motion.div 
                        key={project.id}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.55 + index * 0.05 }}
                        className="group p-4 rounded-xl bg-white/5 hover:bg-white/10 transition-all border border-transparent hover:border-[#D4AF37]/20 cursor-pointer"
                        onClick={() => {
                          setActiveTab('contacts');
                          setExpandedProject(project.id);
                        }}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className={`w-10 h-10 rounded-lg ${status?.color || 'bg-gray-500'}/20 flex items-center justify-center`}>
                            <StatusIcon size={18} className={status?.color.replace('bg-', 'text-').replace('-500', '-400') || 'text-gray-400'} />
                          </div>
                          {project.allocatedBudget && (
                            <span className="text-[#D4AF37] text-sm font-medium">{formatBudget(project.allocatedBudget)}</span>
                          )}
                        </div>
                        <h4 className="text-white font-medium mb-1 truncate group-hover:text-[#D4AF37] transition-colors">
                          {project.projectTitle}
                        </h4>
                        <p className="text-[#71717a] text-sm truncate">{project.name}</p>
                        <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between">
                          <span className={`px-2 py-1 rounded-full text-xs ${status?.color || 'bg-gray-500'}/20 text-white`}>
                            {status?.label || 'Soumission'}
                          </span>
                          <span className="text-[#71717a] text-xs">{project.category}</span>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </motion.div>
            </div>
          ) : activeTab === 'contacts' ? (
            <div className="space-y-6">
              {/* Search Bar */}
              <div className="relative">
                <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#71717a]" />
                <input
                  type="text"
                  value={projectSearch}
                  onChange={(e) => setProjectSearch(e.target.value)}
                  placeholder="Rechercher un projet (titre, producteur, email, catégorie)..."
                  className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-[#71717a] focus:outline-none focus:border-[#D4AF37]/50"
                />
              </div>

              {filteredContacts.length === 0 ? (
                <div className="glass rounded-2xl p-10 text-center">
                  <FileText size={48} className="text-[#71717a] mx-auto mb-4" />
                  <p className="text-[#71717a]">{projectSearch ? 'Aucun projet trouvé' : 'Aucun projet soumis pour le moment'}</p>
                </div>
              ) : (
                filteredContacts.map((contact, index) => {
                  const currentStatusIndex = getStatusIndex(contact.status || 'soumission');
                  const projectDocs = getProjectDocuments(contact.id);
                  const isExpanded = expandedProject === contact.id;
                  
                  return (
                    <motion.div
                      key={contact.id || index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="glass rounded-2xl overflow-hidden"
                    >
                      <div className="p-6">
                        {/* Header */}
                        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
                          <div>
                            <h3 className="text-xl font-medium text-white mb-1">
                              {contact.projectTitle}
                            </h3>
                            <p className="text-[#D4AF37] text-sm">{contact.category}</p>
                            <p className="text-[#71717a] text-sm mt-1">
                              {contact.name} • {contact.email}
                            </p>
                          </div>
                          <div className="flex flex-col items-end gap-2">
                            <div className="flex items-center gap-2 text-[#71717a] text-sm">
                              <Calendar size={14} />
                              {contact.timestamp ? formatDate(contact.timestamp) : 'N/A'}
                            </div>
                            <div className="flex items-center gap-2 flex-wrap justify-end">
                              {projectDocs.length > 0 && (
                                <span className="px-2 py-1 bg-green-500/20 text-green-400 text-xs rounded-full">
                                  {projectDocs.length} document(s)
                                </span>
                              )}
                              {contact.allocatedBudget && (
                                <span className="px-2 py-1 bg-[#D4AF37]/20 text-[#D4AF37] text-xs rounded-full flex items-center gap-1">
                                  <Banknote size={12} />
                                  {contact.allocatedBudget}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Status Pipeline */}
                        <div className="mb-6">
                          <p className="text-[#71717a] text-xs uppercase tracking-wider mb-4">
                            Statut du projet
                          </p>
                          <div className="flex items-center gap-2 overflow-x-auto pb-2">
                            {statusSteps.map((step, i) => {
                              const isActive = i === currentStatusIndex;
                              const isCompleted = i < currentStatusIndex;
                              const StepIcon = step.icon;
                              
                              return (
                                <div key={step.id} className="flex items-center">
                                  <button
                                    onClick={() => updateProjectStatus(contact.id, step.id)}
                                    disabled={updatingProject === contact.id}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all whitespace-nowrap ${
                                      isActive 
                                        ? `${step.color} text-white` 
                                        : isCompleted 
                                          ? 'bg-green-500/20 text-green-400'
                                          : 'bg-white/5 text-[#71717a] hover:bg-white/10 hover:text-white'
                                    }`}
                                  >
                                    {updatingProject === contact.id ? (
                                      <Loader2 size={14} className="animate-spin" />
                                    ) : isCompleted ? (
                                      <CheckCircle size={14} />
                                    ) : (
                                      <StepIcon size={14} />
                                    )}
                                    <span className="text-sm">{step.label}</span>
                                  </button>
                                  {i < statusSteps.length - 1 && (
                                    <ChevronRight size={16} className="text-[#71717a] mx-1 flex-shrink-0" />
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>

                        {/* Access Link */}
                        <div className="mb-6 p-4 rounded-xl bg-white/5 border border-white/10">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <LinkIcon size={18} className="text-[#D4AF37]" />
                              <div>
                                <p className="text-white text-sm font-medium">Lien d'accès producteur</p>
                                {contact.accessToken ? (
                                  <p className="text-[#71717a] text-xs font-mono">
                                    {window.location.origin}/producteur/{contact.accessToken}
                                  </p>
                                ) : (
                                  <p className="text-[#71717a] text-xs">Aucun lien généré</p>
                                )}
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {contact.accessToken ? (
                                <>
                                  <button
                                    onClick={() => copyLink(contact.accessToken)}
                                    className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                                    title="Copier le lien"
                                  >
                                    {copiedToken === contact.accessToken ? (
                                      <CheckCircle size={16} className="text-green-400" />
                                    ) : (
                                      <Copy size={16} className="text-[#D4AF37]" />
                                    )}
                                  </button>
                                  <a
                                    href={`/producteur/${contact.accessToken}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                                    title="Ouvrir"
                                  >
                                    <ExternalLink size={16} className="text-[#D4AF37]" />
                                  </a>
                                </>
                              ) : (
                                <button
                                  onClick={() => generateToken(contact.id)}
                                  disabled={generatingToken === contact.id}
                                  className="px-4 py-2 bg-[#D4AF37] text-[#050505] rounded-lg text-sm font-medium hover:bg-[#F3E5AB] transition-colors flex items-center gap-2"
                                >
                                  {generatingToken === contact.id ? (
                                    <Loader2 size={14} className="animate-spin" />
                                  ) : (
                                    <LinkIcon size={14} />
                                  )}
                                  Générer le lien
                                </button>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Budget Inline */}
                        <div className="mb-6 p-4 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <Euro size={18} className="text-[#D4AF37]" />
                              <div>
                                <p className="text-[#D4AF37] text-sm font-medium">Achat de droits</p>
                                {inlineBudgetEdit === contact.id ? (
                                  <div className="flex items-center gap-2 mt-1">
                                    <input
                                      type="text"
                                      value={inlineBudgetValue}
                                      onChange={(e) => setInlineBudgetValue(e.target.value)}
                                      placeholder="Ex: 15 000 €"
                                      className="bg-white/10 border border-white/20 rounded py-1 px-2 text-white text-sm focus:outline-none w-32"
                                    />
                                    <button
                                      onClick={() => saveInlineBudget(contact.id)}
                                      className="p-1 rounded bg-green-500/20 text-green-400 hover:bg-green-500/30"
                                    >
                                      <CheckCircle size={14} />
                                    </button>
                                    <button
                                      onClick={() => { setInlineBudgetEdit(null); setInlineBudgetValue(''); }}
                                      className="p-1 rounded bg-red-500/20 text-red-400 hover:bg-red-500/30"
                                    >
                                      <X size={14} />
                                    </button>
                                  </div>
                                ) : (
                                  <p className="text-white text-lg font-semibold">
                                    {contact.allocatedBudget || 'Non défini'}
                                  </p>
                                )}
                              </div>
                            </div>
                            {inlineBudgetEdit !== contact.id && (
                              <button
                                onClick={() => {
                                  setInlineBudgetEdit(contact.id);
                                  setInlineBudgetValue(contact.allocatedBudget || '');
                                }}
                                className="p-2 rounded-lg bg-[#D4AF37]/20 hover:bg-[#D4AF37]/30 text-[#D4AF37]"
                              >
                                <Edit2 size={14} />
                              </button>
                            )}
                          </div>
                        </div>

                        {/* Producteurs liés */}
                        <div className="mb-6 p-4 rounded-xl bg-white/5 border border-white/10">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <Users size={16} className="text-blue-400" />
                              <p className="text-white text-sm font-medium">Producteurs liés</p>
                            </div>
                            <button
                              onClick={() => setAddingProducerToProject(addingProducerToProject === contact.id ? null : contact.id)}
                              className="text-[#D4AF37] text-sm hover:text-[#F3E5AB] flex items-center gap-1"
                            >
                              <PlusCircle size={14} />
                              Ajouter
                            </button>
                          </div>
                          
                          {/* Liste des producteurs */}
                          <div className="flex flex-wrap gap-2 mb-2">
                            {(contact.producers || [contact.email]).filter(Boolean).map(email => {
                              const prod = producers.find(p => p.email === email);
                              return (
                                <div key={email} className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-500/20 text-blue-400 text-sm">
                                  <span>{prod?.name || email}</span>
                                  <button
                                    onClick={() => removeProducerFromProject(contact.id, email)}
                                    className="hover:text-red-400"
                                  >
                                    <X size={12} />
                                  </button>
                                </div>
                              );
                            })}
                            {(!contact.producers || contact.producers.length === 0) && !contact.email && (
                              <p className="text-[#71717a] text-sm">Aucun producteur lié</p>
                            )}
                          </div>
                          
                          {/* Formulaire ajout producteur */}
                          {addingProducerToProject === contact.id && (
                            <div className="mt-3 p-3 rounded-lg bg-white/5 border border-white/20">
                              <input
                                type="text"
                                value={producerSearchInProject}
                                onChange={(e) => {
                                  setProducerSearchInProject(e.target.value);
                                  setSelectedProducerToAdd(null);
                                }}
                                placeholder="Rechercher un producteur..."
                                className="w-full bg-white/10 border border-white/20 rounded py-2 px-3 text-white text-sm focus:outline-none mb-2"
                              />
                              <div className="max-h-32 overflow-y-auto space-y-1 mb-3">
                                {getFilteredProducersForProject()
                                  .filter(p => !(contact.producers || []).includes(p.email) && p.email !== contact.email)
                                  .map(p => (
                                  <button
                                    key={p.email}
                                    type="button"
                                    onClick={() => setSelectedProducerToAdd(p)}
                                    className={`w-full text-left px-3 py-2 rounded text-white text-sm flex items-center gap-2 transition-colors ${
                                      selectedProducerToAdd?.email === p.email 
                                        ? 'bg-[#D4AF37]/30 border border-[#D4AF37]' 
                                        : 'bg-white/5 hover:bg-white/10'
                                    }`}
                                  >
                                    <User size={12} className={selectedProducerToAdd?.email === p.email ? 'text-[#D4AF37]' : 'text-[#71717a]'} />
                                    <span>{p.name || p.email}</span>
                                    {p.company && <span className="text-[#71717a] text-xs">• {p.company}</span>}
                                  </button>
                                ))}
                                {getFilteredProducersForProject().filter(p => !(contact.producers || []).includes(p.email) && p.email !== contact.email).length === 0 && (
                                  <p className="text-[#71717a] text-sm text-center py-2">Aucun producteur disponible</p>
                                )}
                              </div>
                              <div className="flex gap-2">
                                <button
                                  onClick={() => {
                                    if (selectedProducerToAdd) {
                                      addProducerToProject(contact.id, selectedProducerToAdd.email);
                                      setSelectedProducerToAdd(null);
                                    }
                                  }}
                                  disabled={!selectedProducerToAdd}
                                  className="flex-1 px-4 py-2 bg-[#D4AF37] text-[#050505] rounded font-medium text-sm hover:bg-[#F3E5AB] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                  <CheckCircle size={14} />
                                  Valider
                                </button>
                                <button
                                  onClick={() => {
                                    setAddingProducerToProject(null);
                                    setProducerSearchInProject('');
                                    setSelectedProducerToAdd(null);
                                  }}
                                  className="px-4 py-2 bg-white/10 text-white rounded text-sm hover:bg-white/20 transition-colors"
                                >
                                  Annuler
                                </button>
                              </div>
                            </div>
                          )}
                        </div>

                        {/* Documents du producteur */}
                        <div className="mb-6 p-4 rounded-xl bg-white/5 border border-white/10">
                          <div className="flex items-center gap-2 mb-3">
                            <FolderOpen size={16} className="text-green-400" />
                            <p className="text-white text-sm font-medium">Documents du producteur ({projectDocs.filter(d => d.uploadedBy === 'producer').length})</p>
                          </div>
                          {projectDocs.filter(d => d.uploadedBy === 'producer').length === 0 ? (
                            <p className="text-[#71717a] text-sm">Aucun document transmis</p>
                          ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              {projectDocs.filter(d => d.uploadedBy === 'producer').map(doc => {
                                const hasFile = !!doc.storedFileName;
                                return hasFile ? (
                                  <a
                                    key={doc.id}
                                    href={`${BACKEND_URL}/api/documents/download/${doc.id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 p-3 rounded-lg bg-green-500/10 hover:bg-green-500/20 transition-colors"
                                  >
                                    <FileCheck size={16} className="text-green-400" />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-white text-sm truncate">{doc.fileName}</p>
                                      <p className="text-green-400 text-xs">{doc.documentType}</p>
                                    </div>
                                    <Download size={14} className="text-green-400" />
                                  </a>
                                ) : (
                                  <div
                                    key={doc.id}
                                    className="flex items-center gap-2 p-3 rounded-lg bg-orange-500/10 border border-orange-500/30"
                                  >
                                    <AlertCircle size={16} className="text-orange-400" />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-white text-sm truncate">{doc.fileName}</p>
                                      <p className="text-orange-400 text-xs">Fichier à ré-uploader</p>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {/* Preuves de virement (multiple) */}
                        <div className="mb-6 p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
                          <div className="flex items-center justify-between mb-3">
                            <div className="flex items-center gap-2">
                              <CreditCard size={16} className="text-emerald-400" />
                              <p className="text-white text-sm font-medium">Preuves de virement</p>
                            </div>
                            <label className="cursor-pointer">
                              <input 
                                type="file" 
                                className="hidden"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={(e) => {
                                  const file = e.target.files?.[0];
                                  if (file) uploadTransferProof(contact.id, file);
                                }}
                                disabled={uploadingTransferProof === contact.id}
                              />
                              <span className="text-emerald-400 text-sm hover:text-emerald-300 flex items-center gap-1 cursor-pointer">
                                {uploadingTransferProof === contact.id ? (
                                  <Loader2 size={14} className="animate-spin" />
                                ) : (
                                  <>
                                    <Upload size={14} />
                                    Ajouter
                                  </>
                                )}
                              </span>
                            </label>
                          </div>
                          {projectDocs.filter(d => d.documentType === 'Preuve de virement').length === 0 ? (
                            <p className="text-[#71717a] text-sm">Aucune preuve de virement</p>
                          ) : (
                            <div className="space-y-2">
                              {projectDocs.filter(d => d.documentType === 'Preuve de virement').map(doc => (
                                <a
                                  key={doc.id}
                                  href={`${BACKEND_URL}/api/documents/download/${doc.id}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-2 p-2 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 transition-colors"
                                >
                                  <FileCheck size={14} className="text-emerald-400" />
                                  <span className="text-white text-sm flex-1">{doc.fileName}</span>
                                  <span className="text-[#71717a] text-xs">{formatDate(doc.uploadedAt)}</span>
                                  <Download size={12} className="text-emerald-400" />
                                </a>
                              ))}
                            </div>
                          )}
                        </div>

                        {/* Rating & Internal Note */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                          {/* Star Rating */}
                          <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                            <p className="text-[#71717a] text-xs mb-2">Note interne</p>
                            <div className="flex items-center gap-1">
                              {[1, 2, 3, 4, 5].map((star) => (
                                <button
                                  key={star}
                                  onClick={() => saveProjectRating(contact.id, star)}
                                  className="p-1 hover:scale-110 transition-transform"
                                >
                                  <Star
                                    size={20}
                                    className={`${
                                      (contact.rating || 0) >= star
                                        ? 'text-[#D4AF37] fill-[#D4AF37]'
                                        : 'text-[#71717a]'
                                    }`}
                                  />
                                </button>
                              ))}
                              {contact.rating > 0 && (
                                <span className="text-[#D4AF37] text-sm ml-2">{contact.rating}/5</span>
                              )}
                            </div>
                          </div>

                          {/* Internal Note */}
                          <div className="p-4 rounded-xl bg-white/5 border border-white/10">
                            <p className="text-[#71717a] text-xs mb-2">Commentaire interne</p>
                            {editingNote === contact.id ? (
                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  value={noteText}
                                  onChange={(e) => setNoteText(e.target.value)}
                                  placeholder="Ajouter une note..."
                                  className="flex-1 bg-white/10 border border-white/20 rounded py-1 px-2 text-white text-sm focus:outline-none"
                                />
                                <button
                                  onClick={() => saveProjectNote(contact.id)}
                                  className="p-1 rounded bg-green-500/20 text-green-400 hover:bg-green-500/30"
                                >
                                  <CheckCircle size={14} />
                                </button>
                                <button
                                  onClick={() => { setEditingNote(null); setNoteText(''); }}
                                  className="p-1 rounded bg-red-500/20 text-red-400 hover:bg-red-500/30"
                                >
                                  <X size={14} />
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center justify-between">
                                <p className="text-[#a1a1aa] text-sm">
                                  {contact.internalNote || 'Aucune note'}
                                </p>
                                <button
                                  onClick={() => {
                                    setEditingNote(contact.id);
                                    setNoteText(contact.internalNote || '');
                                  }}
                                  className="text-[#D4AF37] hover:text-[#F3E5AB]"
                                >
                                  <MessageSquare size={14} />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Expand/Collapse + Delete */}
                        <div className="flex items-center gap-3">
                          <button
                            onClick={() => setExpandedProject(isExpanded ? null : contact.id)}
                            className="text-[#D4AF37] text-sm hover:underline flex items-center gap-1"
                          >
                            {isExpanded ? 'Masquer les détails' : 'Voir les détails'}
                            <ChevronRight size={14} className={`transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                          </button>
                          <button
                            onClick={() => deleteProject(contact.id, contact.projectTitle)}
                            disabled={deletingProject === contact.id}
                            className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-colors"
                            title="Supprimer le projet"
                          >
                            {deletingProject === contact.id ? (
                              <Loader2 size={14} className="animate-spin" />
                            ) : (
                              <Trash2 size={14} />
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Expanded Content */}
                      {isExpanded && (
                        <div className="border-t border-white/10 p-6 bg-white/5">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div>
                              <p className="text-[#71717a] text-xs mb-2">Synopsis</p>
                              <p className="text-[#a1a1aa] text-sm">{contact.synopsis}</p>
                            </div>
                            <div>
                              <p className="text-[#71717a] text-xs mb-2">Documents déposés ({projectDocs.length})</p>
                              {projectDocs.length === 0 ? (
                                <p className="text-[#71717a] text-sm">Aucun document</p>
                              ) : (
                                <div className="space-y-2">
                                  {projectDocs.map(doc => (
                                    <div key={doc.id} className="flex items-center gap-2 p-2 rounded-lg bg-white/5">
                                      <FileText size={14} className="text-[#D4AF37]" />
                                      <span className="text-white text-sm">{doc.fileName}</span>
                                      <span className="text-[#71717a] text-xs">({doc.documentType})</span>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          {contact.videoLink && (
                            <div className="mt-4">
                              <p className="text-[#71717a] text-xs mb-2">Lien vidéo</p>
                              <a href={contact.videoLink} target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:underline text-sm">
                                {contact.videoLink}
                              </a>
                            </div>
                          )}
                        </div>
                      )}
                    </motion.div>
                  );
                })
              )}
            </div>
          ) : activeTab === 'documents' ? (
            <div className="space-y-6">
              {contacts.length === 0 ? (
                <div className="glass rounded-2xl p-10 text-center">
                  <FolderOpen size={48} className="text-[#71717a] mx-auto mb-4" />
                  <p className="text-[#71717a]">Aucun projet pour le moment</p>
                </div>
              ) : (
                contacts.map((project, index) => {
                  const projectDocs = getProjectDocuments(project.id);
                  const producerDocs = projectDocs.filter(d => d.uploadedBy !== 'admin');
                  const adminDocs = projectDocs.filter(d => d.uploadedBy === 'admin');
                  const contractExists = hasContract(project.id);
                  const ribExists = projectDocs.some(d => d.documentType === 'RIB');
                  const isExpanded = expandedDocProject === project.id;
                  
                  return (
                    <motion.div
                      key={project.id || index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="glass rounded-2xl overflow-hidden"
                    >
                      {/* Project Header */}
                      <div 
                        className="p-6 cursor-pointer hover:bg-white/5 transition-colors"
                        onClick={() => setExpandedDocProject(isExpanded ? null : project.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="w-12 h-12 rounded-xl bg-[#D4AF37]/20 flex items-center justify-center">
                              <Film size={24} className="text-[#D4AF37]" />
                            </div>
                            <div>
                              <h3 className="text-lg font-medium text-white">{project.projectTitle}</h3>
                              <p className="text-[#71717a] text-sm">{project.name} • {project.category}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="px-3 py-1 rounded-full text-xs bg-blue-500/20 text-blue-400">
                                {producerDocs.filter(d => d.documentType !== 'RIB').length} doc(s)
                              </span>
                              {ribExists && (
                                <span className="px-3 py-1 rounded-full text-xs bg-amber-500/20 text-amber-400 flex items-center gap-1">
                                  <CreditCard size={12} />
                                  RIB
                                </span>
                              )}
                              {contractExists && (
                                <span className="px-3 py-1 rounded-full text-xs bg-green-500/20 text-green-400 flex items-center gap-1">
                                  <FileCheck size={12} />
                                  Contrat
                                </span>
                              )}
                            </div>
                            <ChevronDown 
                              size={20} 
                              className={`text-[#71717a] transition-transform ${isExpanded ? 'rotate-180' : ''}`} 
                            />
                          </div>
                        </div>
                      </div>

                      {/* Expanded Content */}
                      {isExpanded && (
                        <div className="border-t border-white/10 p-6 bg-white/5">
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                            {/* Documents du producteur */}
                            <div>
                              <h4 className="text-white font-medium mb-4 flex items-center gap-2">
                                <Upload size={16} className="text-blue-400" />
                                Documents du producteur ({producerDocs.length})
                              </h4>
                              {producerDocs.length === 0 ? (
                                <p className="text-[#71717a] text-sm">Aucun document déposé par le producteur</p>
                              ) : (
                                <div className="space-y-2">
                                  {producerDocs.map(doc => {
                                    const isRib = doc.documentType === 'RIB';
                                    return (
                                      <div 
                                        key={doc.id} 
                                        className={`flex items-center justify-between p-3 rounded-lg ${
                                          isRib ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-white/5'
                                        }`}
                                      >
                                        <div className="flex items-center gap-3">
                                          {isRib ? (
                                            <CreditCard size={16} className="text-amber-400" />
                                          ) : (
                                            <FileText size={16} className="text-blue-400" />
                                          )}
                                          <div>
                                            <p className="text-white text-sm">{doc.fileName}</p>
                                            <p className={`text-xs ${isRib ? 'text-amber-400' : 'text-[#71717a]'}`}>
                                              {doc.documentType} • {doc.fileSize}
                                            </p>
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                          <button
                                            onClick={() => handleDownload(doc.id)}
                                            className={`p-2 rounded-lg transition-colors ${
                                              isRib 
                                                ? 'bg-amber-500/20 hover:bg-amber-500/30 text-amber-400' 
                                                : 'bg-blue-500/20 hover:bg-blue-500/30 text-blue-400'
                                            }`}
                                            title="Télécharger"
                                          >
                                            <Download size={14} />
                                          </button>
                                          <span className="text-[#71717a] text-xs">
                                            {doc.uploadedAt ? formatDate(doc.uploadedAt) : ''}
                                          </span>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>

                            {/* Documents Admin (Contrat) */}
                            <div>
                              <h4 className="text-white font-medium mb-4 flex items-center gap-2">
                                <FileCheck size={16} className="text-green-400" />
                                Documents Second Souffle
                              </h4>
                              
                              {/* Contrat existant */}
                              {adminDocs.length > 0 ? (
                                <div className="space-y-2 mb-4">
                                  {adminDocs.map(doc => (
                                    <div key={doc.id} className="flex items-center justify-between p-3 rounded-lg bg-green-500/10 border border-green-500/30">
                                      <div className="flex items-center gap-3">
                                        <FileCheck size={16} className="text-green-400" />
                                        <div>
                                          <p className="text-white text-sm">{doc.fileName}</p>
                                          <p className="text-green-400 text-xs">{doc.documentType} • {doc.fileSize}</p>
                                        </div>
                                      </div>
                                      <div className="flex items-center gap-3">
                                        <button
                                          onClick={() => handleDownload(doc.id)}
                                          className="p-2 rounded-lg bg-green-500/20 hover:bg-green-500/30 text-green-400 transition-colors"
                                          title="Télécharger"
                                        >
                                          <Download size={14} />
                                        </button>
                                        <span className="text-[#71717a] text-xs">
                                          {doc.uploadedAt ? formatDate(doc.uploadedAt) : ''}
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : null}

                              {/* Upload Contrat */}
                              {!contractExists && (
                                <div className="p-4 border-2 border-dashed border-white/20 rounded-xl text-center hover:border-[#D4AF37]/50 transition-colors">
                                  <FileCheck size={24} className="text-[#71717a] mx-auto mb-2" />
                                  <p className="text-[#a1a1aa] text-sm mb-3">
                                    Envoyer le contrat définitif au producteur
                                  </p>
                                  <label className="cursor-pointer">
                                    <input 
                                      type="file" 
                                      className="hidden"
                                      accept=".pdf,.doc,.docx"
                                      onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) uploadContract(project.id, file);
                                      }}
                                      disabled={uploadingContract === project.id}
                                    />
                                    <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#050505] rounded-lg text-sm font-medium hover:bg-[#F3E5AB] transition-colors cursor-pointer">
                                      {uploadingContract === project.id ? (
                                        <>
                                          <Loader2 size={14} className="animate-spin" />
                                          Envoi en cours...
                                        </>
                                      ) : (
                                        <>
                                          <Upload size={14} />
                                          Ajouter le contrat définitif
                                        </>
                                      )}
                                    </span>
                                  </label>
                                </div>
                              )}

                              {contractExists && (
                                <p className="text-green-400 text-sm flex items-center gap-2 mt-2">
                                  <CheckCircle size={14} />
                                  Le contrat est visible par le producteur
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  );
                })
              )}
            </div>
          ) : activeTab === 'producers' ? (
            <div className="space-y-6">
              {/* Search & Create */}
              <div className="flex flex-col md:flex-row gap-4">
                <div className="relative flex-1">
                  <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#71717a]" />
                  <input
                    type="text"
                    value={producerSearch}
                    onChange={(e) => setProducerSearch(e.target.value)}
                    placeholder="Rechercher un producteur (nom, email, société)..."
                    className="w-full pl-12 pr-4 py-3 rounded-xl bg-white/5 border border-white/10 text-white placeholder:text-[#71717a] focus:outline-none focus:border-[#D4AF37]/50"
                  />
                </div>
                <button
                  onClick={() => setShowCreateProducer(!showCreateProducer)}
                  className="flex items-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#050505] rounded-lg font-medium hover:bg-[#F3E5AB] transition-colors whitespace-nowrap"
                >
                  <UserPlus size={18} />
                  Créer un producteur
                </button>
              </div>

              {/* Create Producer Form */}
              {showCreateProducer && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="glass rounded-2xl p-6"
                >
                  <h3 className="text-white font-medium mb-4">Nouveau producteur</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-[#71717a] text-sm mb-2">Email *</label>
                      <input
                        type="email"
                        value={newProducerEmail}
                        onChange={(e) => setNewProducerEmail(e.target.value)}
                        placeholder="email@exemple.com"
                        className="w-full bg-white/5 border border-white/10 rounded-lg py-3 px-4 text-white placeholder:text-[#71717a] focus:outline-none focus:border-[#D4AF37]/50"
                      />
                    </div>
                    <div>
                      <label className="block text-[#71717a] text-sm mb-2">Nom (optionnel)</label>
                      <input
                        type="text"
                        value={newProducerName}
                        onChange={(e) => setNewProducerName(e.target.value)}
                        placeholder="Nom du producteur"
                        className="w-full bg-white/5 border border-white/10 rounded-lg py-3 px-4 text-white placeholder:text-[#71717a] focus:outline-none focus:border-[#D4AF37]/50"
                      />
                    </div>
                    <div className="flex items-end">
                      <button
                        onClick={createProducer}
                        disabled={!newProducerEmail || creatingProducer}
                        className="w-full py-3 bg-[#D4AF37] text-[#050505] rounded-lg font-medium hover:bg-[#F3E5AB] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                      >
                        {creatingProducer ? (
                          <Loader2 size={18} className="animate-spin" />
                        ) : (
                          <>
                            <UserPlus size={18} />
                            Créer et copier le lien
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                  <p className="text-[#71717a] text-xs mt-3">
                    Le lien de connexion sera automatiquement copié dans votre presse-papiers.
                  </p>
                </motion.div>
              )}

              {/* Producers List */}
              {filteredProducers.length === 0 ? (
                <div className="glass rounded-2xl p-10 text-center">
                  <Users size={48} className="text-[#71717a] mx-auto mb-4" />
                  <p className="text-[#71717a]">{producerSearch ? 'Aucun producteur trouvé' : 'Aucun producteur pour le moment'}</p>
                </div>
              ) : (
                filteredProducers.map((producer, index) => {
                  const producerProjects = contacts.filter(c => 
                    c.email?.toLowerCase() === producer.email?.toLowerCase()
                  );
                  const isAddingToThis = showAddProject === producer.email;
                  
                  return (
                    <motion.div
                      key={producer.email || index}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="glass rounded-2xl overflow-hidden"
                    >
                      <div className="p-6">
                        {/* Producer Info */}
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-4">
                            <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                              producer.producerType === 'societe' ? 'bg-purple-500/20' : 'bg-blue-500/20'
                            }`}>
                              {producer.producerType === 'societe' ? (
                                <Building size={24} className="text-purple-400" />
                              ) : (
                                <User size={24} className="text-blue-400" />
                              )}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <h3 className="text-lg font-medium text-white">{producer.name || producer.email}</h3>
                                {producer.producerType === 'societe' && (
                                  <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 text-xs rounded">Société</span>
                                )}
                              </div>
                              {producer.name && <p className="text-[#71717a] text-sm">{producer.email}</p>}
                              <div className="flex items-center gap-3 mt-1">
                                {producer.phone && <span className="text-[#a1a1aa] text-xs">{producer.phone}</span>}
                                {producer.company && <span className="text-[#a1a1aa] text-xs">• {producer.company}</span>}
                                {producer.siret && <span className="text-[#a1a1aa] text-xs">• SIRET: {producer.siret}</span>}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {/* Budget Total */}
                            <div className="text-right mr-2">
                              <p className="text-[#71717a] text-xs">Budget total</p>
                              <p className="text-[#D4AF37] font-semibold flex items-center gap-1">
                                {formatBudget(getProducerTotalBudget(producer.email))}
                              </p>
                            </div>
                            <button
                              onClick={() => openProducerProfile(producer)}
                              className="p-2 rounded-lg bg-[#D4AF37]/20 hover:bg-[#D4AF37]/30 text-[#D4AF37] transition-colors"
                              title="Voir/Modifier le profil"
                            >
                              <Edit2 size={16} />
                            </button>
                            <button
                              onClick={() => copyProducerLink(producer.accessToken)}
                              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                              title="Copier le lien"
                            >
                              {copiedProducerToken === producer.accessToken ? (
                                <CheckCircle size={16} className="text-green-400" />
                              ) : (
                                <Copy size={16} className="text-[#D4AF37]" />
                              )}
                            </button>
                            <a
                              href={`/producteur/${producer.accessToken}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
                              title="Ouvrir l'espace"
                            >
                              <ExternalLink size={16} className="text-[#a1a1aa]" />
                            </a>
                            <button
                              onClick={() => deleteProducer(producer.email, producer.name)}
                              disabled={deletingProducer === producer.email}
                              className="p-2 rounded-lg bg-red-500/10 hover:bg-red-500/20 text-red-400 transition-colors"
                              title="Supprimer le producteur"
                            >
                              {deletingProducer === producer.email ? (
                                <Loader2 size={16} className="animate-spin" />
                              ) : (
                                <Trash2 size={16} />
                              )}
                            </button>
                          </div>
                        </div>

                        {/* Projects List */}
                        <div className="mb-4">
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-[#71717a] text-sm">Projets ({producerProjects.length})</p>
                            <button
                              onClick={() => setShowAddProject(isAddingToThis ? null : producer.email)}
                              className="text-[#D4AF37] text-sm hover:text-[#F3E5AB] flex items-center gap-1"
                            >
                              <Film size={14} />
                              Ajouter un projet
                            </button>
                          </div>
                          
                          {producerProjects.length > 0 ? (
                            <div className="space-y-2">
                              {producerProjects.map(project => (
                                <div key={project.id} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                                  <div className="flex items-center gap-3">
                                    <Film size={14} className="text-[#D4AF37]" />
                                    <span className="text-white text-sm">{project.projectTitle}</span>
                                    <span className="text-[#71717a] text-xs">({project.category})</span>
                                  </div>
                                  <span className={`px-2 py-1 rounded text-xs ${
                                    project.status === 'diffusion' ? 'bg-green-500/20 text-green-400' :
                                    project.status === 'virement' ? 'bg-emerald-500/20 text-emerald-400' :
                                    project.status === 'production' ? 'bg-purple-500/20 text-purple-400' :
                                    'bg-white/10 text-[#a1a1aa]'
                                  }`}>
                                    {statusSteps.find(s => s.id === project.status)?.label || 'Soumission'}
                                  </span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-[#71717a] text-sm">Aucun projet</p>
                          )}
                        </div>

                        {/* Add Project Form */}
                        {isAddingToThis && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            className="pt-4 border-t border-white/10"
                          >
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                              <input
                                type="text"
                                value={newProjectTitle}
                                onChange={(e) => setNewProjectTitle(e.target.value)}
                                placeholder="Titre du projet"
                                className="bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-white placeholder:text-[#71717a] focus:outline-none focus:border-[#D4AF37]/50 text-sm"
                              />
                              <select
                                value={newProjectCategory}
                                onChange={(e) => setNewProjectCategory(e.target.value)}
                                className="bg-[#1a1a1a] border border-white/10 rounded-lg py-2 px-3 text-white focus:outline-none focus:border-[#D4AF37]/50 text-sm"
                              >
                                <option value="">Catégorie</option>
                                {categories.map(cat => (
                                  <option key={cat} value={cat}>{cat}</option>
                                ))}
                              </select>
                              <button
                                onClick={() => addProjectToProducer(producer.email)}
                                disabled={!newProjectTitle || addingProject}
                                className="py-2 bg-[#D4AF37] text-[#050505] rounded-lg text-sm font-medium hover:bg-[#F3E5AB] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                              >
                                {addingProject ? (
                                  <Loader2 size={14} className="animate-spin" />
                                ) : (
                                  'Ajouter'
                                )}
                              </button>
                            </div>
                          </motion.div>
                        )}

                        {/* Meta Info */}
                        <div className="flex items-center gap-4 text-xs text-[#71717a] pt-3 border-t border-white/10">
                          <span>
                            {producer.password ? (
                              <span className="flex items-center gap-1 text-green-400">
                                <CheckCircle size={12} />
                                MDP défini
                              </span>
                            ) : 'MDP non défini'}
                          </span>
                          <span>Créé: {producer.createdAt ? formatDate(producer.createdAt) : 'N/A'}</span>
                        </div>
                      </div>
                    </motion.div>
                  );
                })
              )}
            </div>
          ) : activeTab === 'donations' ? (
            /* Donations Tab */
            <div className="space-y-6">
              {/* Header with filter and export */}
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <select
                    value={donationYearFilter}
                    onChange={(e) => setDonationYearFilter(e.target.value)}
                    className="bg-white/5 border border-white/10 rounded-lg py-2.5 px-4 text-white focus:outline-none focus:border-[#D4AF37]/50"
                  >
                    <option value="all" className="bg-[#0a0a0a]">Depuis le début</option>
                    {getDonationYears().map(year => (
                      <option key={year} value={year} className="bg-[#0a0a0a]">{year}</option>
                    ))}
                  </select>
                  <span className="text-[#71717a] text-sm">
                    {getFilteredDonations().length} don{getFilteredDonations().length > 1 ? 's' : ''} 
                    {donationYearFilter !== 'all' ? ` en ${donationYearFilter}` : ' au total'}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setShowAddDonation(!showAddDonation)}
                    className="flex items-center gap-2 px-4 py-2.5 bg-green-500/20 text-green-400 rounded-lg font-medium hover:bg-green-500/30 transition-colors"
                  >
                    <PlusCircle size={18} />
                    Ajouter un don
                  </button>
                  <button
                    onClick={exportDonationsToExcel}
                    className="flex items-center gap-2 px-4 py-2.5 bg-[#D4AF37] text-[#050505] rounded-lg font-medium hover:bg-[#E6C762] transition-colors"
                  >
                    <Download size={18} />
                    Exporter en Excel
                  </button>
                  {/* Import Excel Button */}
                  <label className="flex items-center gap-2 px-4 py-2.5 bg-blue-500/20 text-blue-400 rounded-lg font-medium hover:bg-blue-500/30 transition-colors cursor-pointer">
                    <Upload size={18} />
                    {importingExcel ? 'Import en cours...' : 'Importer Excel'}
                    <input
                      type="file"
                      accept=".xlsx,.xls"
                      className="hidden"
                      disabled={importingExcel}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        
                        setImportingExcel(true);
                        try {
                          const formData = new FormData();
                          formData.append('file', file);
                          
                          const response = await axios.post(`${BACKEND_URL}/api/donations/import-excel`, formData, {
                            headers: { 'Content-Type': 'multipart/form-data' }
                          });
                          
                          if (response.data.success) {
                            alert(`Import réussi !\n\n✅ ${response.data.imported} dons importés\n⏭️ ${response.data.skipped} ignorés (doublons ou invalides)\n📊 Total lignes: ${response.data.total_rows}`);
                            fetchData();
                          }
                        } catch (err) {
                          console.error('Import error:', err);
                          alert('Erreur lors de l\'import: ' + (err.response?.data?.detail || err.message));
                        } finally {
                          setImportingExcel(false);
                          e.target.value = ''; // Reset file input
                        }
                      }}
                    />
                  </label>
                </div>
              </div>

              {/* Add Donation Form */}
              {showAddDonation && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  className="glass rounded-2xl p-6"
                >
                  <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                    <PlusCircle size={18} className="text-green-400" />
                    Nouveau don manuel
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Type */}
                    <div>
                      <label className="block text-[#71717a] text-sm mb-2">Type de donateur</label>
                      <select
                        value={newDonation.donorType}
                        onChange={(e) => setNewDonation({...newDonation, donorType: e.target.value})}
                        className="w-full bg-white/5 border border-white/10 rounded-lg py-2.5 px-3 text-white focus:outline-none focus:border-[#D4AF37]/50"
                      >
                        <option value="individual" className="bg-[#0a0a0a]">Particulier</option>
                        <option value="company" className="bg-[#0a0a0a]">Entreprise</option>
                      </select>
                    </div>
                    {/* Amount */}
                    <div>
                      <label className="block text-[#71717a] text-sm mb-2">Montant (€) *</label>
                      <input
                        type="number"
                        value={newDonation.amount}
                        onChange={(e) => setNewDonation({...newDonation, amount: e.target.value})}
                        placeholder="1000"
                        className="w-full bg-white/5 border border-white/10 rounded-lg py-2.5 px-3 text-white placeholder:text-[#71717a] focus:outline-none focus:border-[#D4AF37]/50"
                      />
                    </div>
                    {/* Status */}
                    <div>
                      <label className="block text-[#71717a] text-sm mb-2">Statut</label>
                      <select
                        value={newDonation.status}
                        onChange={(e) => setNewDonation({...newDonation, status: e.target.value})}
                        className="w-full bg-white/5 border border-white/10 rounded-lg py-2.5 px-3 text-white focus:outline-none focus:border-[#D4AF37]/50"
                      >
                        <option value="confirmed" className="bg-[#0a0a0a]">Confirmé</option>
                        <option value="pending" className="bg-[#0a0a0a]">En attente</option>
                      </select>
                    </div>
                    {/* Date */}
                    <div>
                      <label className="block text-[#71717a] text-sm mb-2">Date du don</label>
                      <input
                        type="date"
                        value={newDonation.createdAt}
                        onChange={(e) => setNewDonation({...newDonation, createdAt: e.target.value})}
                        className="w-full bg-white/5 border border-white/10 rounded-lg py-2.5 px-3 text-white focus:outline-none focus:border-[#D4AF37]/50"
                      />
                    </div>
                    {/* First Name */}
                    <div>
                      <label className="block text-[#71717a] text-sm mb-2">Prénom *</label>
                      <input
                        type="text"
                        value={newDonation.firstName}
                        onChange={(e) => setNewDonation({...newDonation, firstName: e.target.value})}
                        placeholder="Jean"
                        className="w-full bg-white/5 border border-white/10 rounded-lg py-2.5 px-3 text-white placeholder:text-[#71717a] focus:outline-none focus:border-[#D4AF37]/50"
                      />
                    </div>
                    {/* Last Name */}
                    <div>
                      <label className="block text-[#71717a] text-sm mb-2">Nom *</label>
                      <input
                        type="text"
                        value={newDonation.lastName}
                        onChange={(e) => setNewDonation({...newDonation, lastName: e.target.value})}
                        placeholder="Dupont"
                        className="w-full bg-white/5 border border-white/10 rounded-lg py-2.5 px-3 text-white placeholder:text-[#71717a] focus:outline-none focus:border-[#D4AF37]/50"
                      />
                    </div>
                    {/* Email */}
                    <div>
                      <label className="block text-[#71717a] text-sm mb-2">Email</label>
                      <input
                        type="email"
                        value={newDonation.email}
                        onChange={(e) => setNewDonation({...newDonation, email: e.target.value})}
                        placeholder="jean@example.com"
                        className="w-full bg-white/5 border border-white/10 rounded-lg py-2.5 px-3 text-white placeholder:text-[#71717a] focus:outline-none focus:border-[#D4AF37]/50"
                      />
                    </div>
                    {/* Phone */}
                    <div>
                      <label className="block text-[#71717a] text-sm mb-2">Téléphone</label>
                      <input
                        type="tel"
                        value={newDonation.phone}
                        onChange={(e) => setNewDonation({...newDonation, phone: e.target.value})}
                        placeholder="06 12 34 56 78"
                        className="w-full bg-white/5 border border-white/10 rounded-lg py-2.5 px-3 text-white placeholder:text-[#71717a] focus:outline-none focus:border-[#D4AF37]/50"
                      />
                    </div>
                    {/* Company fields (if company) */}
                    {newDonation.donorType === 'company' && (
                      <>
                        <div>
                          <label className="block text-[#71717a] text-sm mb-2">Société</label>
                          <input
                            type="text"
                            value={newDonation.companyName}
                            onChange={(e) => setNewDonation({...newDonation, companyName: e.target.value})}
                            placeholder="Ma Société SAS"
                            className="w-full bg-white/5 border border-white/10 rounded-lg py-2.5 px-3 text-white placeholder:text-[#71717a] focus:outline-none focus:border-[#D4AF37]/50"
                          />
                        </div>
                        <div>
                          <label className="block text-[#71717a] text-sm mb-2">SIRET</label>
                          <input
                            type="text"
                            value={newDonation.siret}
                            onChange={(e) => setNewDonation({...newDonation, siret: e.target.value})}
                            placeholder="123 456 789 00012"
                            className="w-full bg-white/5 border border-white/10 rounded-lg py-2.5 px-3 text-white placeholder:text-[#71717a] focus:outline-none focus:border-[#D4AF37]/50"
                          />
                        </div>
                      </>
                    )}
                    {/* Address */}
                    <div>
                      <label className="block text-[#71717a] text-sm mb-2">Adresse</label>
                      <input
                        type="text"
                        value={newDonation.address}
                        onChange={(e) => setNewDonation({...newDonation, address: e.target.value})}
                        placeholder="123 Rue Example"
                        className="w-full bg-white/5 border border-white/10 rounded-lg py-2.5 px-3 text-white placeholder:text-[#71717a] focus:outline-none focus:border-[#D4AF37]/50"
                      />
                    </div>
                    {/* Postal Code */}
                    <div>
                      <label className="block text-[#71717a] text-sm mb-2">Code postal</label>
                      <input
                        type="text"
                        value={newDonation.postalCode}
                        onChange={(e) => setNewDonation({...newDonation, postalCode: e.target.value})}
                        placeholder="75001"
                        className="w-full bg-white/5 border border-white/10 rounded-lg py-2.5 px-3 text-white placeholder:text-[#71717a] focus:outline-none focus:border-[#D4AF37]/50"
                      />
                    </div>
                    {/* City */}
                    <div>
                      <label className="block text-[#71717a] text-sm mb-2">Ville</label>
                      <input
                        type="text"
                        value={newDonation.city}
                        onChange={(e) => setNewDonation({...newDonation, city: e.target.value})}
                        placeholder="Paris"
                        className="w-full bg-white/5 border border-white/10 rounded-lg py-2.5 px-3 text-white placeholder:text-[#71717a] focus:outline-none focus:border-[#D4AF37]/50"
                      />
                    </div>
                  </div>
                  {/* Actions */}
                  <div className="flex justify-end gap-3 mt-6">
                    <button
                      onClick={() => {
                        setShowAddDonation(false);
                        setNewDonation({
                          amount: '',
                          donorType: 'individual',
                          firstName: '',
                          lastName: '',
                          email: '',
                          phone: '',
                          address: '',
                          city: '',
                          postalCode: '',
                          country: 'France',
                          companyName: '',
                          siret: '',
                          status: 'confirmed',
                          createdAt: new Date().toISOString().split('T')[0]
                        });
                      }}
                      className="px-4 py-2.5 bg-white/5 text-white rounded-lg font-medium hover:bg-white/10 transition-colors"
                    >
                      Annuler
                    </button>
                    <button
                      onClick={async () => {
                        if (!newDonation.amount || !newDonation.firstName || !newDonation.lastName) {
                          alert('Veuillez remplir les champs obligatoires (Montant, Prénom, Nom)');
                          return;
                        }
                        setAddingDonation(true);
                        try {
                          await axios.post(`${BACKEND_URL}/api/donations/manual`, {
                            ...newDonation,
                            amount: parseFloat(newDonation.amount)
                          });
                          fetchData();
                          setShowAddDonation(false);
                          setNewDonation({
                            amount: '',
                            donorType: 'individual',
                            firstName: '',
                            lastName: '',
                            email: '',
                            phone: '',
                            address: '',
                            city: '',
                            postalCode: '',
                            country: 'France',
                            companyName: '',
                            siret: '',
                            status: 'confirmed',
                            createdAt: new Date().toISOString().split('T')[0]
                          });
                        } catch (err) {
                          console.error('Error adding donation:', err);
                          alert('Erreur lors de l\'ajout du don');
                        } finally {
                          setAddingDonation(false);
                        }
                      }}
                      disabled={addingDonation}
                      className="px-6 py-2.5 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors flex items-center gap-2 disabled:opacity-50"
                    >
                      {addingDonation ? (
                        <><Loader2 size={16} className="animate-spin" /> Ajout...</>
                      ) : (
                        <><PlusCircle size={16} /> Ajouter le don</>
                      )}
                    </button>
                  </div>
                </motion.div>
              )}

              {/* Stats */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="glass rounded-2xl p-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center">
                      <Euro size={20} className="text-[#D4AF37]" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-white">{getYearDonationStats().totalAmount.toLocaleString('fr-FR')} €</p>
                      <p className="text-[#71717a] text-sm">Total collecté</p>
                    </div>
                  </div>
                </div>
                <div className="glass rounded-2xl p-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-green-500/20 flex items-center justify-center">
                      <CheckCircle size={20} className="text-green-400" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-white">{getYearDonationStats().confirmedAmount.toLocaleString('fr-FR')} €</p>
                      <p className="text-[#71717a] text-sm">Confirmés</p>
                    </div>
                  </div>
                </div>
                <div className="glass rounded-2xl p-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-orange-500/20 flex items-center justify-center">
                      <Clock size={20} className="text-orange-400" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-white">{getYearDonationStats().pendingAmount.toLocaleString('fr-FR')} €</p>
                      <p className="text-[#71717a] text-sm">En attente</p>
                    </div>
                  </div>
                </div>
                <div className="glass rounded-2xl p-5">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-500/20 flex items-center justify-center">
                      <Heart size={20} className="text-blue-400" />
                    </div>
                    <div>
                      <p className="text-xl font-bold text-white">{getYearDonationStats().count}</p>
                      <p className="text-[#71717a] text-sm">Nombre de dons</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Donations List */}
              <div className="glass rounded-2xl overflow-hidden">
                {getFilteredDonations().length === 0 ? (
                  <div className="p-10 text-center">
                    <Heart size={48} className="text-[#71717a] mx-auto mb-4" />
                    <p className="text-[#71717a]">Aucun don {donationYearFilter !== 'all' ? `en ${donationYearFilter}` : 'pour le moment'}</p>
                    <p className="text-[#71717a] text-sm mt-2">
                      Partagez votre page <a href="/faire-un-don" target="_blank" className="text-[#D4AF37] hover:underline">/faire-un-don</a>
                    </p>
                  </div>
                ) : (
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left p-4 text-[#71717a] text-sm font-medium">Donateur</th>
                        <th className="text-left p-4 text-[#71717a] text-sm font-medium">Montant</th>
                        <th className="text-left p-4 text-[#71717a] text-sm font-medium">Date</th>
                        <th className="text-left p-4 text-[#71717a] text-sm font-medium">Statut</th>
                        <th className="text-left p-4 text-[#71717a] text-sm font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getFilteredDonations().map((donation) => (
                        <tr 
                          key={donation.id} 
                          className="border-b border-white/5 hover:bg-white/5 cursor-pointer"
                          onClick={() => setSelectedDonation(donation)}
                        >
                          <td className="p-4">
                            <div>
                              <p className="text-white font-medium">{donation.firstName} {donation.lastName}</p>
                              {donation.companyName && (
                                <p className="text-blue-400 text-sm">{donation.companyName}</p>
                              )}
                              <p className="text-[#D4AF37] text-sm">{donation.email}</p>
                            </div>
                          </td>
                          <td className="p-4">
                            <span className="text-[#D4AF37] font-bold text-lg">{donation.amount?.toLocaleString('fr-FR')} €</span>
                          </td>
                          <td className="p-4 text-[#a1a1aa]">
                            {donation.createdAt ? new Date(donation.createdAt).toLocaleDateString('fr-FR', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            }) : 'N/A'}
                          </td>
                          <td className="p-4">
                            {donation.status === 'confirmed' ? (
                              <span className="px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-sm flex items-center gap-1 w-fit">
                                <CheckCircle size={14} />
                                Confirmé
                              </span>
                            ) : donation.status === 'cancelled' ? (
                              <span className="px-3 py-1 rounded-full bg-red-500/20 text-red-400 text-sm flex items-center gap-1 w-fit">
                                <XCircle size={14} />
                                Annulé
                              </span>
                            ) : (
                              <span className="px-3 py-1 rounded-full bg-orange-500/20 text-orange-400 text-sm flex items-center gap-1 w-fit">
                                <Clock size={14} />
                                En attente
                              </span>
                            )}
                          </td>
                          <td className="p-4" onClick={(e) => e.stopPropagation()}>
                            <div className="flex items-center gap-2">
                              {donation.status === 'pending' && (
                                <>
                                  <button
                                    onClick={async () => {
                                      if (window.confirm(`Confirmer la réception du don de ${donation.amount}€ ?`)) {
                                        await axios.patch(`${BACKEND_URL}/api/donations/${donation.id}/confirm`);
                                        fetchData();
                                      }
                                    }}
                                    className="px-3 py-1.5 bg-green-500/20 text-green-400 rounded-lg text-sm hover:bg-green-500/30 transition-colors flex items-center gap-1"
                                  >
                                    <CheckCircle size={14} />
                                    Confirmer
                                  </button>
                                  <button
                                    onClick={async () => {
                                      if (window.confirm(`Annuler ce don ?`)) {
                                        await axios.patch(`${BACKEND_URL}/api/donations/${donation.id}/cancel`);
                                        fetchData();
                                      }
                                    }}
                                    className="px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg text-sm hover:bg-red-500/30 transition-colors"
                                  >
                                    <XCircle size={14} />
                                  </button>
                                </>
                              )}
                              <button
                                onClick={async () => {
                                  if (window.confirm(`Supprimer définitivement ce don ?`)) {
                                    await axios.delete(`${BACKEND_URL}/api/donations/${donation.id}`);
                                    fetchData();
                                  }
                                }}
                                className="px-3 py-1.5 bg-white/5 text-[#71717a] rounded-lg text-sm hover:bg-white/10 transition-colors"
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Donation Detail Modal */}
              {selectedDonation && (
                <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={() => { setSelectedDonation(null); setEditingDonation(false); }}>
                  <div className="bg-[#0a0a0a] rounded-2xl border border-white/10 max-w-lg w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                    {/* Header */}
                    <div className="p-6 border-b border-white/10 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-[#D4AF37]/20 flex items-center justify-center">
                          <Heart size={24} className="text-[#D4AF37]" />
                        </div>
                        <div>
                          <h2 className="text-xl font-semibold text-white">
                            {editingDonation ? (editedDonation.firstName + ' ' + editedDonation.lastName) : (selectedDonation.firstName + ' ' + selectedDonation.lastName)}
                          </h2>
                          <p className="text-[#D4AF37]">{selectedDonation.amount?.toLocaleString('fr-FR')} €</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {!editingDonation ? (
                          <button
                            onClick={() => {
                              setEditedDonation({
                                firstName: selectedDonation.firstName || '',
                                lastName: selectedDonation.lastName || '',
                                address: selectedDonation.address || '',
                                city: selectedDonation.city || '',
                                postalCode: selectedDonation.postalCode || '',
                                country: selectedDonation.country || 'France',
                                phone: selectedDonation.phone || '',
                                companyName: selectedDonation.companyName || '',
                                siret: selectedDonation.siret || ''
                              });
                              setEditingDonation(true);
                            }}
                            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                            title="Modifier"
                          >
                            <Edit2 size={18} className="text-[#D4AF37]" />
                          </button>
                        ) : null}
                        <button
                          onClick={() => { setSelectedDonation(null); setEditingDonation(false); }}
                          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                        >
                          <X size={20} className="text-[#71717a]" />
                        </button>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-4">
                      {/* Status */}
                      <div className="flex items-center justify-between p-4 rounded-xl bg-white/5">
                        <span className="text-[#a1a1aa]">Statut</span>
                        {selectedDonation.status === 'confirmed' ? (
                          <span className="px-3 py-1 rounded-full bg-green-500/20 text-green-400 text-sm flex items-center gap-1">
                            <CheckCircle size={14} />
                            Confirmé
                          </span>
                        ) : selectedDonation.status === 'cancelled' ? (
                          <span className="px-3 py-1 rounded-full bg-red-500/20 text-red-400 text-sm flex items-center gap-1">
                            <XCircle size={14} />
                            Annulé
                          </span>
                        ) : (
                          <span className="px-3 py-1 rounded-full bg-orange-500/20 text-orange-400 text-sm flex items-center gap-1">
                            <Clock size={14} />
                            En attente
                          </span>
                        )}
                      </div>

                      {/* Type */}
                      <div className="flex items-center justify-between p-4 rounded-xl bg-white/5">
                        <span className="text-[#a1a1aa]">Type</span>
                        <span className="text-white flex items-center gap-2">
                          {selectedDonation.donorType === 'company' ? (
                            <><Building size={16} className="text-blue-400" /> Entreprise</>
                          ) : (
                            <><User size={16} className="text-[#D4AF37]" /> Particulier</>
                          )}
                        </span>
                      </div>

                      {/* Editable Fields */}
                      {editingDonation ? (
                        <div className="space-y-4">
                          {/* Name fields */}
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="block text-[#71717a] text-sm mb-1">Prénom</label>
                              <input
                                type="text"
                                value={editedDonation.firstName}
                                onChange={(e) => setEditedDonation({...editedDonation, firstName: e.target.value})}
                                className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-white focus:outline-none focus:border-[#D4AF37]/50"
                              />
                            </div>
                            <div>
                              <label className="block text-[#71717a] text-sm mb-1">Nom</label>
                              <input
                                type="text"
                                value={editedDonation.lastName}
                                onChange={(e) => setEditedDonation({...editedDonation, lastName: e.target.value})}
                                className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-white focus:outline-none focus:border-[#D4AF37]/50"
                              />
                            </div>
                          </div>

                          {/* Phone */}
                          <div>
                            <label className="block text-[#71717a] text-sm mb-1">Téléphone</label>
                            <input
                              type="tel"
                              value={editedDonation.phone}
                              onChange={(e) => setEditedDonation({...editedDonation, phone: e.target.value})}
                              placeholder="06 12 34 56 78"
                              className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-white placeholder:text-[#71717a] focus:outline-none focus:border-[#D4AF37]/50"
                            />
                          </div>

                          {/* Address */}
                          <div>
                            <label className="block text-[#71717a] text-sm mb-1">Adresse</label>
                            <input
                              type="text"
                              value={editedDonation.address}
                              onChange={(e) => setEditedDonation({...editedDonation, address: e.target.value})}
                              className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-white focus:outline-none focus:border-[#D4AF37]/50"
                            />
                          </div>

                          {/* City & Postal Code */}
                          <div className="grid grid-cols-3 gap-3">
                            <div>
                              <label className="block text-[#71717a] text-sm mb-1">Code postal</label>
                              <input
                                type="text"
                                value={editedDonation.postalCode}
                                onChange={(e) => setEditedDonation({...editedDonation, postalCode: e.target.value})}
                                className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-white focus:outline-none focus:border-[#D4AF37]/50"
                              />
                            </div>
                            <div className="col-span-2">
                              <label className="block text-[#71717a] text-sm mb-1">Ville</label>
                              <input
                                type="text"
                                value={editedDonation.city}
                                onChange={(e) => setEditedDonation({...editedDonation, city: e.target.value})}
                                className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-white focus:outline-none focus:border-[#D4AF37]/50"
                              />
                            </div>
                          </div>

                          {/* Company fields (if company) */}
                          {selectedDonation.donorType === 'company' && (
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="block text-[#71717a] text-sm mb-1">Société</label>
                                <input
                                  type="text"
                                  value={editedDonation.companyName}
                                  onChange={(e) => setEditedDonation({...editedDonation, companyName: e.target.value})}
                                  className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-white focus:outline-none focus:border-[#D4AF37]/50"
                                />
                              </div>
                              <div>
                                <label className="block text-[#71717a] text-sm mb-1">SIRET</label>
                                <input
                                  type="text"
                                  value={editedDonation.siret}
                                  onChange={(e) => setEditedDonation({...editedDonation, siret: e.target.value})}
                                  className="w-full bg-white/5 border border-white/10 rounded-lg py-2 px-3 text-white focus:outline-none focus:border-[#D4AF37]/50"
                                />
                              </div>
                            </div>
                          )}

                          {/* Save / Cancel buttons */}
                          <div className="flex gap-3 pt-2">
                            <button
                              onClick={() => setEditingDonation(false)}
                              className="flex-1 py-2 bg-white/5 text-white rounded-lg font-medium hover:bg-white/10 transition-colors"
                            >
                              Annuler
                            </button>
                            <button
                              onClick={async () => {
                                setSavingDonation(true);
                                try {
                                  const res = await axios.put(`${BACKEND_URL}/api/donations/${selectedDonation.id}`, editedDonation);
                                  if (res.data.success) {
                                    setSelectedDonation(res.data.donation);
                                    setEditingDonation(false);
                                    fetchData();
                                  }
                                } catch (err) {
                                  console.error('Error updating donation:', err);
                                  alert('Erreur lors de la sauvegarde');
                                } finally {
                                  setSavingDonation(false);
                                }
                              }}
                              disabled={savingDonation}
                              className="flex-1 py-2 bg-[#D4AF37] text-[#050505] rounded-lg font-medium hover:bg-[#E6C762] transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                            >
                              {savingDonation ? (
                                <><Loader2 size={16} className="animate-spin" /> Sauvegarde...</>
                              ) : (
                                <><Save size={16} /> Enregistrer</>
                              )}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          {/* Company info (view mode) */}
                          {selectedDonation.companyName && (
                            <div className="p-4 rounded-xl bg-blue-500/10 border border-blue-500/20">
                              <div className="flex items-center gap-2 mb-2">
                                <Building size={16} className="text-blue-400" />
                                <span className="text-blue-400 font-medium">Entreprise</span>
                              </div>
                              <p className="text-white">{selectedDonation.companyName}</p>
                              {selectedDonation.siret && (
                                <p className="text-[#71717a] text-sm">SIRET: {selectedDonation.siret}</p>
                              )}
                            </div>
                          )}

                          {/* Contact (view mode) */}
                          <div className="p-4 rounded-xl bg-white/5 space-y-3">
                            <div className="flex items-center gap-3">
                              <Mail size={16} className="text-[#D4AF37]" />
                              <a href={`mailto:${selectedDonation.email}`} className="text-[#D4AF37] hover:underline">
                                {selectedDonation.email}
                              </a>
                            </div>
                            {selectedDonation.phone && (
                              <div className="flex items-center gap-3">
                                <Phone size={16} className="text-[#71717a]" />
                                <a href={`tel:${selectedDonation.phone}`} className="text-white hover:text-[#D4AF37]">
                                  {selectedDonation.phone}
                                </a>
                              </div>
                            )}
                            <div className="flex items-start gap-3">
                              <MapPin size={16} className="text-[#71717a] mt-0.5" />
                              <div className="text-[#a1a1aa]">
                                <p>{selectedDonation.address}</p>
                                <p>{selectedDonation.postalCode} {selectedDonation.city}</p>
                                <p>{selectedDonation.country}</p>
                              </div>
                            </div>
                          </div>

                          {/* Date */}
                          <div className="flex items-center justify-between p-4 rounded-xl bg-white/5">
                            <span className="text-[#a1a1aa]">Date du don</span>
                            <span className="text-white">
                              {selectedDonation.createdAt ? new Date(selectedDonation.createdAt).toLocaleDateString('fr-FR', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                              }) : 'N/A'}
                            </span>
                          </div>

                          {/* Message */}
                          {selectedDonation.message && (
                            <div className="p-4 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/20">
                              <div className="flex items-center gap-2 mb-2">
                                <MessageSquare size={16} className="text-[#D4AF37]" />
                                <span className="text-[#D4AF37] font-medium">Message</span>
                              </div>
                              <p className="text-white italic">&quot;{selectedDonation.message}&quot;</p>
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Actions (only show when not editing) */}
                    {!editingDonation && (
                      <div className="p-6 border-t border-white/10 space-y-3">
                        <div className="flex gap-3">
                          {selectedDonation.status === 'pending' && (
                            <button
                              onClick={async () => {
                                if (window.confirm(`Confirmer la réception du don de ${selectedDonation.amount}€ ?`)) {
                                  await axios.patch(`${BACKEND_URL}/api/donations/${selectedDonation.id}/confirm`);
                                  fetchData();
                                  setSelectedDonation(null);
                                }
                              }}
                              className="flex-1 py-3 bg-green-500/20 text-green-400 rounded-xl font-medium hover:bg-green-500/30 transition-colors flex items-center justify-center gap-2"
                            >
                              <CheckCircle size={18} />
                              Confirmer le don
                            </button>
                          )}
                          <a
                            href={`mailto:${selectedDonation.email}`}
                            className="flex-1 py-3 bg-[#D4AF37] text-[#050505] rounded-xl font-medium hover:bg-[#E6C762] transition-colors flex items-center justify-center gap-2"
                          >
                            <Mail size={18} />
                            Contacter
                          </a>
                        </div>
                        {/* Resend email button */}
                        <button
                          onClick={async () => {
                            setResendingEmail(true);
                            try {
                              await axios.post(`${BACKEND_URL}/api/donations/${selectedDonation.id}/resend-email`);
                              alert(`Email de rappel envoyé à ${selectedDonation.email}`);
                            } catch (err) {
                              alert('Erreur lors de l\'envoi du mail');
                              console.error(err);
                            } finally {
                              setResendingEmail(false);
                            }
                          }}
                          disabled={resendingEmail}
                          className="w-full py-3 bg-white/5 text-white rounded-xl font-medium hover:bg-white/10 transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                          {resendingEmail ? (
                            <>
                              <RefreshCw size={18} className="animate-spin" />
                              Envoi en cours...
                            </>
                          ) : (
                            <>
                              <RefreshCw size={18} />
                              Renvoyer le mail de rappel
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : activeTab === 'budgets' ? (
            <div className="space-y-6">
              <div className="glass rounded-2xl p-6">
                <h3 className="text-white font-medium mb-4 flex items-center gap-2">
                  <Banknote size={20} className="text-[#D4AF37]" />
                  Projets avec Budget Alloué
                </h3>
                <p className="text-[#71717a] text-sm mb-6">
                  Projets en phase de production ou au-delà avec un budget validé.
                </p>
                
                {contacts.filter(c => ['production', 'virement', 'diffusion'].includes(c.status)).length === 0 ? (
                  <div className="text-center py-10">
                    <Banknote size={48} className="text-[#71717a] mx-auto mb-4" />
                    <p className="text-[#71717a]">Aucun projet en production pour le moment</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {contacts
                      .filter(c => ['production', 'virement', 'diffusion'].includes(c.status))
                      .map((project, index) => {
                        const transferProofExists = hasTransferProof(project.id);
                        const isEditingBudget = editingBudget === project.id;
                        
                        return (
                          <motion.div
                            key={project.id}
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: index * 0.05 }}
                            className="p-4 rounded-xl bg-white/5 border border-white/10"
                          >
                            <div className="flex items-center justify-between mb-3">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-[#D4AF37]/20 flex items-center justify-center">
                                  <Film size={20} className="text-[#D4AF37]" />
                                </div>
                                <div>
                                  <h4 className="text-white font-medium">{project.projectTitle}</h4>
                                  <p className="text-[#71717a] text-sm">{project.name} • {project.category}</p>
                                </div>
                              </div>
                              <span className={`px-3 py-1 rounded-full text-xs ${
                                project.status === 'diffusion' ? 'bg-green-500/20 text-green-400' :
                                project.status === 'virement' ? 'bg-emerald-500/20 text-emerald-400' :
                                'bg-purple-500/20 text-purple-400'
                              }`}>
                                {statusSteps.find(s => s.id === project.status)?.label}
                              </span>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              {/* Budget Alloué */}
                              <div className="p-3 rounded-lg bg-white/5">
                                <p className="text-[#71717a] text-xs mb-1">Budget alloué</p>
                                {isEditingBudget ? (
                                  <div className="flex items-center gap-2">
                                    <input
                                      type="text"
                                      value={budgetValue}
                                      onChange={(e) => setBudgetValue(e.target.value)}
                                      placeholder="Ex: 50 000 €"
                                      className="flex-1 bg-white/10 border border-white/20 rounded py-1 px-2 text-white text-sm focus:outline-none"
                                    />
                                    <button
                                      onClick={() => updateBudget(project.id, budgetValue)}
                                      className="p-1 rounded bg-green-500/20 text-green-400 hover:bg-green-500/30"
                                    >
                                      <CheckCircle size={14} />
                                    </button>
                                  </div>
                                ) : (
                                  <div className="flex items-center justify-between">
                                    <span className="text-white font-medium">
                                      {project.allocatedBudget || 'Non défini'}
                                    </span>
                                    <button
                                      onClick={() => {
                                        setEditingBudget(project.id);
                                        setBudgetValue(project.allocatedBudget || '');
                                      }}
                                      className="text-[#D4AF37] hover:text-[#F3E5AB]"
                                    >
                                      <FileText size={14} />
                                    </button>
                                  </div>
                                )}
                              </div>
                              
                              {/* Status Virement */}
                              <div className="p-3 rounded-lg bg-white/5">
                                <p className="text-[#71717a] text-xs mb-1">Virement</p>
                                {project.status === 'virement' || project.status === 'diffusion' ? (
                                  <span className="text-emerald-400 text-sm flex items-center gap-1">
                                    <CheckCircle size={14} />
                                    Émis
                                  </span>
                                ) : (
                                  <span className="text-[#71717a] text-sm">En attente</span>
                                )}
                              </div>
                              
                              {/* Preuve de Virement */}
                              <div className="p-3 rounded-lg bg-white/5">
                                <p className="text-[#71717a] text-xs mb-1">Preuve de virement</p>
                                {transferProofExists ? (
                                  <span className="text-green-400 text-sm flex items-center gap-1">
                                    <FileCheck size={14} />
                                    Déposée
                                  </span>
                                ) : (
                                  <label className="cursor-pointer">
                                    <input 
                                      type="file" 
                                      className="hidden"
                                      accept=".pdf,.jpg,.jpeg,.png"
                                      onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) uploadTransferProof(project.id, file);
                                      }}
                                      disabled={uploadingTransferProof === project.id}
                                    />
                                    <span className="text-[#D4AF37] text-sm hover:text-[#F3E5AB] flex items-center gap-1 cursor-pointer">
                                      {uploadingTransferProof === project.id ? (
                                        <Loader2 size={14} className="animate-spin" />
                                      ) : (
                                        <>
                                          <Upload size={14} />
                                          Déposer
                                        </>
                                      )}
                                    </span>
                                  </label>
                                )}
                              </div>
                            </div>
                          </motion.div>
                        );
                      })}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </main>

      {/* Producer Profile Modal */}
      {selectedProducer && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-black/80 backdrop-blur-sm"
            onClick={() => setSelectedProducer(null)}
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="relative glass rounded-2xl p-8 max-w-3xl w-full max-h-[90vh] overflow-y-auto"
          >
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className={`w-16 h-16 rounded-xl flex items-center justify-center ${
                  editedProducer.producerType === 'societe' ? 'bg-purple-500/20' : 'bg-blue-500/20'
                }`}>
                  {editedProducer.producerType === 'societe' ? (
                    <Building size={32} className="text-purple-400" />
                  ) : (
                    <User size={32} className="text-blue-400" />
                  )}
                </div>
                <div>
                  <h2 className="text-2xl font-medium text-white">{selectedProducer.name || 'Producteur'}</h2>
                  <p className="text-[#a1a1aa]">{selectedProducer.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setEditingProducerProfile(!editingProducerProfile)}
                  className={`p-2 rounded-lg transition-colors ${
                    editingProducerProfile ? 'bg-amber-500/20 text-amber-400' : 'bg-white/5 text-white hover:bg-white/10'
                  }`}
                >
                  <Edit2 size={18} />
                </button>
                <button
                  onClick={() => setSelectedProducer(null)}
                  className="p-2 rounded-lg bg-white/5 hover:bg-white/10 text-white"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Type de producteur */}
            {editingProducerProfile && (
              <div className="mb-6 p-4 rounded-xl bg-white/5 border border-white/10">
                <p className="text-[#71717a] text-xs mb-3">Type de producteur</p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setEditedProducer({...editedProducer, producerType: 'particulier'})}
                    className={`flex-1 py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors ${
                      editedProducer.producerType !== 'societe'
                        ? 'bg-blue-500/20 border border-blue-500/50 text-blue-400'
                        : 'bg-white/5 border border-white/10 text-white'
                    }`}
                  >
                    <User size={16} />
                    Particulier
                  </button>
                  <button
                    onClick={() => setEditedProducer({...editedProducer, producerType: 'societe'})}
                    className={`flex-1 py-2 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors ${
                      editedProducer.producerType === 'societe'
                        ? 'bg-purple-500/20 border border-purple-500/50 text-purple-400'
                        : 'bg-white/5 border border-white/10 text-white'
                    }`}
                  >
                    <Building size={16} />
                    Société
                  </button>
                </div>
              </div>
            )}

            {/* Contact Info */}
            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="p-4 rounded-xl bg-white/5">
                <div className="flex items-center gap-2 mb-1">
                  <User size={14} className="text-[#71717a]" />
                  <span className="text-[#71717a] text-xs">Nom / Prénom</span>
                </div>
                {editingProducerProfile ? (
                  <input
                    type="text"
                    value={editedProducer.name || ''}
                    onChange={(e) => setEditedProducer({...editedProducer, name: e.target.value})}
                    className="w-full bg-white/10 border border-white/20 rounded py-1 px-2 text-white text-sm focus:outline-none"
                  />
                ) : (
                  <p className="text-white">{selectedProducer.name || 'Non renseigné'}</p>
                )}
              </div>
              <div className="p-4 rounded-xl bg-white/5">
                <div className="flex items-center gap-2 mb-1">
                  <Phone size={14} className="text-[#71717a]" />
                  <span className="text-[#71717a] text-xs">Téléphone</span>
                </div>
                {editingProducerProfile ? (
                  <input
                    type="tel"
                    value={editedProducer.phone || ''}
                    onChange={(e) => setEditedProducer({...editedProducer, phone: e.target.value})}
                    className="w-full bg-white/10 border border-white/20 rounded py-1 px-2 text-white text-sm focus:outline-none"
                  />
                ) : (
                  <p className="text-white">{selectedProducer.phone || 'Non renseigné'}</p>
                )}
              </div>
              <div className="p-4 rounded-xl bg-white/5">
                <div className="flex items-center gap-2 mb-1">
                  <Building size={14} className="text-[#71717a]" />
                  <span className="text-[#71717a] text-xs">Société</span>
                </div>
                {editingProducerProfile ? (
                  <input
                    type="text"
                    value={editedProducer.company || ''}
                    onChange={(e) => setEditedProducer({...editedProducer, company: e.target.value})}
                    className="w-full bg-white/10 border border-white/20 rounded py-1 px-2 text-white text-sm focus:outline-none"
                  />
                ) : (
                  <p className="text-white">{selectedProducer.company || 'Non renseigné'}</p>
                )}
              </div>
              <div className="p-4 rounded-xl bg-white/5">
                <div className="flex items-center gap-2 mb-1">
                  <User size={14} className="text-[#71717a]" />
                  <span className="text-[#71717a] text-xs">Gérant</span>
                </div>
                {editingProducerProfile ? (
                  <input
                    type="text"
                    value={editedProducer.director || ''}
                    onChange={(e) => setEditedProducer({...editedProducer, director: e.target.value})}
                    className="w-full bg-white/10 border border-white/20 rounded py-1 px-2 text-white text-sm focus:outline-none"
                  />
                ) : (
                  <p className="text-white">{selectedProducer.director || 'Non renseigné'}</p>
                )}
              </div>
              <div className="p-4 rounded-xl bg-white/5">
                <div className="flex items-center gap-2 mb-1">
                  <MapPin size={14} className="text-[#71717a]" />
                  <span className="text-[#71717a] text-xs">Adresse</span>
                </div>
                {editingProducerProfile ? (
                  <input
                    type="text"
                    value={editedProducer.address || ''}
                    onChange={(e) => setEditedProducer({...editedProducer, address: e.target.value})}
                    className="w-full bg-white/10 border border-white/20 rounded py-1 px-2 text-white text-sm focus:outline-none"
                  />
                ) : (
                  <p className="text-white">{selectedProducer.address || 'Non renseigné'}</p>
                )}
              </div>
              {(editingProducerProfile || editedProducer.producerType === 'societe') && (
                <div className="p-4 rounded-xl bg-white/5">
                  <div className="flex items-center gap-2 mb-1">
                    <FileText size={14} className="text-[#71717a]" />
                    <span className="text-[#71717a] text-xs">SIRET</span>
                  </div>
                  {editingProducerProfile ? (
                    <input
                      type="text"
                      value={editedProducer.siret || ''}
                      onChange={(e) => setEditedProducer({...editedProducer, siret: e.target.value})}
                      placeholder="123 456 789 00012"
                      className="w-full bg-white/10 border border-white/20 rounded py-1 px-2 text-white text-sm focus:outline-none"
                    />
                  ) : (
                    <p className="text-white">{selectedProducer.siret || 'Non renseigné'}</p>
                  )}
                </div>
              )}
            </div>

            {/* Save Button */}
            {editingProducerProfile && (
              <button
                onClick={saveProducerProfile}
                disabled={savingProducer}
                className="w-full mb-6 py-3 bg-[#D4AF37] text-[#050505] rounded-lg font-medium hover:bg-[#F3E5AB] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {savingProducer ? <Loader2 size={18} className="animate-spin" /> : <Save size={18} />}
                Enregistrer les modifications
              </button>
            )}

            {/* Budget Total */}
            <div className="p-6 rounded-xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 mb-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#D4AF37] text-sm font-medium mb-1">Budget total alloué</p>
                  <p className="text-3xl font-bold text-white">
                    {formatBudget(getProducerTotalBudget(selectedProducer.email))}
                  </p>
                </div>
                <Euro size={40} className="text-[#D4AF37]" />
              </div>
            </div>

            {/* Projects List */}
            <div className="mb-6">
              <p className="text-[#71717a] text-sm mb-3">
                Projets ({contacts.filter(c => c.email?.toLowerCase() === selectedProducer.email?.toLowerCase()).length})
              </p>
              <div className="space-y-2">
                {contacts
                  .filter(c => c.email?.toLowerCase() === selectedProducer.email?.toLowerCase())
                  .map(project => (
                    <div key={project.id} className="flex items-center justify-between p-3 rounded-lg bg-white/5">
                      <div className="flex items-center gap-3">
                        <Film size={16} className="text-[#D4AF37]" />
                        <span className="text-white">{project.projectTitle}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        {project.allocatedBudget && (
                          <span className="text-[#D4AF37] text-sm">{project.allocatedBudget}</span>
                        )}
                        <span className={`px-2 py-1 rounded text-xs ${
                          project.status === 'diffusion' ? 'bg-green-500/20 text-green-400' :
                          project.status === 'virement' ? 'bg-emerald-500/20 text-emerald-400' :
                          project.status === 'production' ? 'bg-purple-500/20 text-purple-400' :
                          'bg-white/10 text-[#a1a1aa]'
                        }`}>
                          {statusSteps.find(s => s.id === project.status)?.label || 'Soumission'}
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>

            {/* Admin Comment */}
            <div className="p-4 rounded-xl bg-white/5 border border-white/10">
              <div className="flex items-center gap-2 mb-3">
                <MessageSquare size={16} className="text-[#D4AF37]" />
                <p className="text-white font-medium">Commentaire administrateur</p>
              </div>
              <textarea
                value={producerComment}
                onChange={(e) => setProducerComment(e.target.value)}
                placeholder="Ajouter un commentaire sur ce producteur..."
                rows={3}
                className="w-full bg-white/5 border border-white/10 rounded-lg py-3 px-4 text-white placeholder:text-[#71717a] focus:outline-none focus:border-[#D4AF37]/50 resize-none mb-3"
              />
              <button
                onClick={saveProducerComment}
                disabled={savingComment}
                className="px-4 py-2 bg-[#D4AF37] text-[#050505] rounded-lg text-sm font-medium hover:bg-[#F3E5AB] transition-colors flex items-center gap-2"
              >
                {savingComment ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <>
                    <CheckCircle size={14} />
                    Enregistrer
                  </>
                )}
              </button>
            </div>
          </motion.div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default AdminPage;
